// ============================================================================
// TIMETRACKER PRO - SUPABASE HOOKS (FIELD-LOCKED)
// ============================================================================
// Generated from approved field list
// Every field explicitly mapped - no omissions
// RLS-COMPLIANT: All write operations require Supabase session
// ============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabaseClient';
import { applyTimelineDiff, protectTrackingFields } from '../utils/timelineDiff';
import { resolveProjectNames, projectDisplayList, diffIds } from '../utils/vendorProjectLinks';

// Per-project queue for updateProjectTimelineDiff (see there).
const timelineDiffQueue = new Map();

// ============================================================================
// SESSION GUARD HELPER
// ============================================================================
// Ensures all write operations have a valid Supabase session.
// This is REQUIRED for RLS policies to work correctly.
// Note: The app's "switch user" feature only changes the UI context,
// it does NOT change the Supabase auth session.
// ============================================================================

/**
 * Require an active Supabase session for write operations.
 * Returns the session's user.id (UUID) for use in auth_user_id/created_by fields.
 * Throws a user-friendly error if no session exists.
 * 
 * @returns {Promise<string>} The authenticated user's UUID
 * @throws {Error} If no valid session exists
 */
const requireSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError) {
    console.error('[requireSession] Session error:', sessionError);
    throw new Error('Authentication error: ' + sessionError.message);
  }
  
  if (!session || !session.user) {
    console.error('[requireSession] No active Supabase session');
    throw new Error(
      'Please sign in to perform this action. ' +
      'Note: The "switch user" feature only changes the UI view — ' +
      'it does not change your database permissions. ' +
      'You must sign in with your actual credentials.'
    );
  }
  
  return session.user.id;
};

// ============================================================================
// SUPABASE ERROR FORMATTER
// ============================================================================
// Converts cryptic Postgres/Supabase errors into actionable user messages.
// ============================================================================

/**
 * Format a Supabase/Postgres error into a user-friendly message.
 * 
 * @param {Error|object} error - The error object from Supabase
 * @param {object} context - Context about the operation
 * @param {string} context.action - What was attempted (create, update, delete, fetch)
 * @param {string} context.entity - What entity (time entry, project, team, etc.)
 * @param {string} [context.role] - Current user role (for permission messages)
 * @returns {string} User-friendly error message
 */
export const formatSupabaseError = (error, context = {}) => {
  const { action = 'perform action', entity = 'record', role } = context;
  const code = error?.code;
  const message = error?.message || String(error);
  const hint = error?.hint;

  // PostgreSQL error codes
  
  // 42501 - RLS permission denied
  if (code === '42501') {
    const roleHint = role ? ` Your role (${role}) may not have permission.` : '';
    return `Permission denied: Cannot ${action} ${entity}.${roleHint} Contact an admin if needed.`;
  }
  
  // 42703 - undefined_column
  if (code === '42703') {
    return `Database schema mismatch when trying to ${action} ${entity}. The app may need updating.`;
  }
  
  // 23503 - foreign_key_violation
  if (code === '23503') {
    if (action === 'delete') {
      return `Cannot ${action} ${entity}: Another record is still linked to it. Remove the link first.`;
    }
    return `Cannot ${action} ${entity}: A referenced record (project, user, or team) doesn't exist.`;
  }
  
  // 23505 - unique_violation
  if (code === '23505') {
    return `Cannot ${action} ${entity}: A record with this identifier already exists.`;
  }
  
  // 23502 - not_null_violation
  if (code === '23502') {
    return `Cannot ${action} ${entity}: Required data is missing.`;
  }
  
  // 22P02 - invalid_text_representation (type mismatch)
  if (code === '22P02') {
    return `Invalid data format when trying to ${action} ${entity}. Check that IDs are correct.`;
  }
  
  // P0001 - custom trigger error
  if (code === 'P0001') {
    return `Database rule violation: ${message || `Cannot ${action} ${entity}`}`;
  }
  
  // PGRST errors
  if (code?.startsWith('PGRST')) {
    if (code === 'PGRST116') return `No ${entity} found.`;
    return `API error: Cannot ${action} ${entity}. ${hint || message}`;
  }
  
  // Network errors
  if (message?.includes('fetch') || message?.includes('network') || message?.includes('Failed to fetch')) {
    return `Network error: Cannot reach the server. Check your connection and try again.`;
  }
  
  // Session / auth errors — match specific patterns to avoid false positives
  // (e.g. "Auth session missing" should match, but "Please sign in to perform this action" should not)
  if (
    message?.includes('JWT') ||
    message?.includes('refresh token') ||
    message?.includes('Auth session') ||
    message?.includes('token is expired') ||
    message?.includes('token expired') ||
    message?.includes('session expired')
  ) {
    return `Session expired: Please sign in again.`;
  }
  
  // Default with original message
  if (message && message !== '[object Object]') {
    return `Failed to ${action} ${entity}: ${message}`;
  }
  return `Failed to ${action} ${entity}`;
};

// ============================================================================
// ID NORMALIZATION HELPERS
// ============================================================================
// User IDs in the app are numeric (BIGINT from DB).
// Supabase may return supervisor_ids as strings in JSONB.
// These helpers ensure consistent comparison by normalizing to numbers.
// ============================================================================

/**
 * Normalize a single ID to a number for consistent comparison.
 * Handles: number, numeric string, null/undefined
 * @param {any} val - The ID value to normalize
 * @returns {number|null} - Normalized ID as number, or null
 */
export const normalizeId = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    // If it looks like an integer string, convert to number
    if (/^-?\d+$/.test(val.trim())) {
      return parseInt(val, 10);
    }
    // For UUIDs or other string IDs, return as-is (shouldn't happen for user IDs)
    return val;
  }
  return val;
};

/**
 * Normalize an array of IDs to numbers for consistent comparison.
 * @param {Array} arr - Array of IDs to normalize
 * @returns {Array} - Array of normalized IDs (numbers)
 */
export const normalizeIdArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map(normalizeId).filter(id => id !== null);
};

/**
 * Type-safe includes check that normalizes both the array and the search value.
 * Use this instead of Array.includes() when comparing IDs from different sources.
 * @param {Array} arr - Array of IDs (will be normalized)
 * @param {any} id - ID to search for (will be normalized)
 * @returns {boolean}
 */
export const normalizedIncludes = (arr, id) => {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  const normalizedId = normalizeId(id);
  if (normalizedId === null) return false;
  return arr.some(item => normalizeId(item) === normalizedId);
};

// ============================================================================
// DROPBOX TEAM-PORTRAIT FOLDER SYNC
// ============================================================================

/**
 * Ask the sync-team-portraits edge function to reconcile one member's Dropbox
 * portrait folder.
 *
 * Every member has a folder under
 *   /BRIO-press/00_Practice/Portraits & Team/Portraits/{Active|Non-Active}/
 * holding their current portrait, an Originals/ folder of earlier versions, and
 * their bio document. Switching somebody between active and non-active has to
 * move that folder, with everything in it, to the other bucket — which is what
 * this call does.
 *
 * Never awaited and never throws. The Postgres write has already succeeded by
 * the time this runs, and Dropbox is the slower, less reliable half: making the
 * user's save hang on it — or fail because of it — would be the wrong trade.
 * A failure is self-healing rather than lost, because the next full sync from
 * Comms & Assets finds the folder in the wrong bucket and moves it then.
 */
export const syncTeamPortraitFolder = (userId) => {
  supabase.auth.getSession()
    .then(({ data: { session } }) => fetch(`${SUPABASE_URL}/functions/v1/sync-team-portraits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify({ userIds: [normalizeId(userId)] }),
    }))
    .catch(err => console.warn('[syncTeamPortraitFolder] deferred to the next full sync:', err?.message));
};

// ============================================================================
// 1. USERS HOOK (17 fields — 13 from users + 4 from user_profiles)
// ============================================================================
export const useSupabaseUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, role, job_role, job_level, team_id, markup, contracted_hours, join_date, end_date, office_location, is_active, auth_user_id, status, bonuses, temp_access_pages, temp_access_expires_at')
        .is('deleted_at', null)
        .order('name');
      
      if (error) throw error;
      
      // ── Fetch profile dates for employment window + calendar ──
      // FK: user_profiles.user_id → users.id  (NOT auth_user_id)
      // Also fetch joining_date as start-date fallback
      const { data: profiles, error: profileErr } = await supabase
        .from('user_profiles')
        .select('user_id, dob, leaving_date, joining_date');

      // ── Fetch role history entries active as of today ──
      // The users.job_role/job_level columns are updated by DB trigger from
      // user_role_history and may reflect FUTURE entries. We query the table
      // directly to find the entry effective on today's date.
      const today = new Date().toISOString().split('T')[0];
      const { data: roleHistoryRows } = await supabase
        .from('user_role_history')
        .select('user_id, job_role, job_level, nl_schaal, nl_trede, effective_from, effective_to')
        .lte('effective_from', today)
        .or(`effective_to.is.null,effective_to.gte.${today}`);

      // Build roleMap: per user, pick the entry with the latest effective_from
      const roleMap = {};
      (roleHistoryRows || []).forEach(r => {
        const uid = String(r.user_id);
        if (!roleMap[uid] || r.effective_from > roleMap[uid].effective_from) {
          roleMap[uid] = { jobRole: r.job_role, jobLevel: r.job_level, nlSchaal: r.nl_schaal, nlTrede: r.nl_trede, effective_from: r.effective_from };
        }
      });
      
      if (profileErr) {
        console.error(
          '[useSupabaseUsers] ⚠️ user_profiles query FAILED — leaving_date/joining_date will be null for all users.',
          profileErr.message, profileErr.details || '', profileErr.hint || ''
        );
      }
      
      // Build profileMap keyed by user_id (bigint → string coerced by JS)
      const profileMap = {};
      (profiles || []).forEach(p => {
        if (p.user_id != null) {
          profileMap[p.user_id] = {
            dob: p.dob || null,
            leavingDate: p.leaving_date || null,
            joiningDate: p.joining_date || null,
          };
        }
      });
      
      // Dev: diagnose profile fetch health
      if (process.env.NODE_ENV !== 'production') {
        const profileRows = (profiles || []).length;
        const userRows = (data || []).length;
        const mapKeys = Object.keys(profileMap).length;
        // Check match rate: how many users have a profile entry?
        let matchCount = 0;
        let sampleMiss = null;
        (data || []).forEach(u => {
          if (profileMap[u.id]) {
            matchCount++;
          } else if (!sampleMiss) {
            sampleMiss = { id: u.id, name: u.name, type: typeof u.id };
          }
        });
        console.log(
          `[useSupabaseUsers] Profiles: ${profileRows} rows fetched, ${mapKeys} map keys, ` +
          `${matchCount}/${userRows} users matched` +
          (profileErr ? ' ⚠️ QUERY FAILED' : '') +
          (profileRows > 0 && matchCount === 0 ? ' ⚠️ ZERO MATCHES — possible FK mismatch (user_id vs id types?)' : '')
        );
        if (sampleMiss && profileRows > 0) {
          const sampleProfileKey = Object.keys(profileMap)[0];
          console.warn(
            `[useSupabaseUsers] Sample miss: user ${sampleMiss.name} id=${sampleMiss.id} (${sampleMiss.type}), ` +
            `first profileMap key=${sampleProfileKey} (${typeof sampleProfileKey})`
          );
        }
        // Check if users table has end_date column
        if (data && data.length > 0) {
          const hasEndDateCol = 'end_date' in data[0];
          const withEndDate = data.filter(u => u.end_date != null).length;
          const withLeavingDate = Object.values(profileMap).filter(p => p.leavingDate != null).length;
          console.log(
            `[useSupabaseUsers] Employment end: users.end_date column ${hasEndDateCol ? 'EXISTS' : '⚠️ MISSING'}, ` +
            `${withEndDate} users have end_date, ${withLeavingDate} profiles have leaving_date`
          );
        }
      }
      
      // Map from snake_case (DB) to camelCase (App)
      const mapped = (data || []).map(u => {
        const profile = profileMap[u.id] || {};
        const effectiveRole = roleMap[String(u.id)];
        return {
          id: normalizeId(u.id),                  // required (normalized for comparisons)
          name: u.name,                           // required
          email: u.email,                         // required
          role: u.role,                           // required
          jobRole: u.job_role,                    // optional, nullable (may include future entries)
          jobLevel: u.job_level,                  // optional, nullable (may include future entries)
          // Role active on today's date (from user_role_history filtered to today)
          effectiveJobRole: effectiveRole?.jobRole ?? u.job_role,
          effectiveJobLevel: effectiveRole?.jobLevel ?? u.job_level,
          effectiveNlSchaal: effectiveRole?.nlSchaal || null,
          effectiveNlTrede: effectiveRole?.nlTrede ?? null,
          markup: u.markup,                       // optional
          teamId: normalizeId(u.team_id),         // optional, nullable (normalized)
          contractedHours: u.contracted_hours ?? 176, // monthly contracted hours, default full-time
          // ── Employment dates ──────────────────────
          // Start: users.join_date (authoritative), user_profiles.joining_date (fallback)
          // End:   users.end_date (authoritative), user_profiles.leaving_date (fallback)
          // NOTE: users.start_date is corrupt and deliberately excluded
          joinDate: u.join_date || null,
          joiningDate: profile.joiningDate || null,
          endDate: u.end_date || null,
          leavingDate: profile.leavingDate || null,
          // ──────────────────────────────────────────
          officeLocation: u.office_location || 'NL', // optional, default: 'NL'
          isActive: u.is_active !== false,        // optional, default: true (Phase 3.4)
          authUserId: u.auth_user_id || null,     // Auth linkage (for ledger matching + password reset)
          status: u.status || null,               // User lifecycle status (invited/active/inactive)
          bonuses: u.bonuses || null,              // JSONB: { "2026": { q1, q2, q3, q4 }, ... }
          // Admin-granted temporary page access (see canAccessPage in App.js)
          tempAccessPages: Array.isArray(u.temp_access_pages) ? u.temp_access_pages : [],
          tempAccessExpiresAt: u.temp_access_expires_at || null,
          // Merged from user_profiles for calendar categories
          dob: profile.dob || null,
        };
      });
      
      setUsers(mapped);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();

    // Realtime subscription: apply delta updates from payload when possible,
    // falling back to full refetch for INSERT/DELETE (rare operations).
    const channel = supabase
      .channel('users-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'users' }, (payload) => {
        if (payload.eventType === 'UPDATE' && payload.new) {
          const u = payload.new;
          setUsers(prev => prev.map(existing =>
            existing.id === normalizeId(u.id)
              ? {
                  ...existing, // preserve merged profile/role fields
                  name: u.name,
                  email: u.email,
                  role: u.role,
                  jobRole: u.job_role,
                  jobLevel: u.job_level,
                  markup: u.markup,
                  teamId: normalizeId(u.team_id),
                  contractedHours: u.contracted_hours ?? 176,
                  joinDate: u.join_date || null,
                  endDate: u.end_date || null,
                  officeLocation: u.office_location || 'NL',
                  isActive: u.is_active !== false,
                  authUserId: u.auth_user_id || null,
                  status: u.status || null,
                  bonuses: u.bonuses || null,
                  tempAccessPages: Array.isArray(u.temp_access_pages) ? u.temp_access_pages : [],
                  tempAccessExpiresAt: u.temp_access_expires_at || null,
                }
              : existing
          ));
        } else {
          // INSERT/DELETE: full refetch to pick up profile + role data
          fetchUsers();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const createUser = async (userData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Map ALL 11 fields from camelCase (App) to snake_case (DB)
      // Handle empty strings for date fields
      // NOTE: id is NOT sent - let the database generate it (BIGINT GENERATED)
      // NOTE:
// Project ownership is defined via team_projects.
// Supervisors see projects linked to teams they supervise.
// Do NOT infer ownership from time entries or allocations.
      const dbData = {
        name: userData.name,                                // required
        email: userData.email,                              // required
        role: userData.role ?? 'user',                      // required, default: 'user'
        job_role: userData.jobRole || null,                 // optional, nullable (|| catches empty string)
        job_level: userData.jobLevel ?? null,               // optional, nullable
        markup: userData.markup ?? 0,                       // optional, default: 0
        team_id: userData.teamId ?? null,                   // optional, nullable
        join_date: userData.joinDate || null,               // optional, nullable (|| catches empty string)
        office_location: userData.officeLocation || 'NL', // optional, default: 'NL'
        is_active: userData.isActive !== false              // optional, default: true (Phase 3.4)
      };

      const { data, error } = await supabase
        .from('users')
        .insert([dbData])
        .select()
        .single();
      
      if (error) throw error;

      // Give the new member their Dropbox portrait folder now, so there is
      // somewhere to drop their photos and bio before anyone runs a full sync.
      if (data?.id) syncTeamPortraitFolder(data.id);

      await fetchUsers();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating user:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'user' }) };
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Guard against accidental undefined/null IDs
      if (userId === undefined || userId === null || userId === '') {
        throw new Error('updateUser called without a valid userId');
      }

      // If IDs are numeric in your DB but strings in UI, coerce safely
      const idValue = (typeof userId === 'string' && /^\d+$/.test(userId)) ? Number(userId) : userId;

      // Map from camelCase (App) to snake_case (DB)
      const dbUpdates = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.email !== undefined) dbUpdates.email = updates.email;
      if (updates.role !== undefined) dbUpdates.role = updates.role;
      if (updates.markup !== undefined) dbUpdates.markup = updates.markup;
      if (updates.teamId !== undefined) dbUpdates.team_id = updates.teamId;
      // NOTE: jobRole/jobLevel are READ-ONLY snapshot fields managed exclusively
      // by the user_role_history trigger. Do NOT write them in updateUser.
      // if (updates.jobRole !== undefined) dbUpdates.job_role = updates.jobRole;
      // if (updates.jobLevel !== undefined) dbUpdates.job_level = updates.jobLevel;
      if (updates.profileHistory !== undefined) dbUpdates.profile_history = updates.profileHistory;
      if (updates.annualLeaveEntitlement !== undefined) dbUpdates.annual_leave_entitlement = updates.annualLeaveEntitlement;
      if (updates.officeLocation !== undefined) dbUpdates.office_location = updates.officeLocation;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
      if (updates.joinDate !== undefined) dbUpdates.join_date = updates.joinDate || null;
      if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate || null;
      if (updates.bonuses !== undefined) dbUpdates.bonuses = updates.bonuses;
      if (updates.tempAccessPages !== undefined) {
        dbUpdates.temp_access_pages = Array.isArray(updates.tempAccessPages) ? updates.tempAccessPages : [];
      }
      if (updates.tempAccessExpiresAt !== undefined) {
        dbUpdates.temp_access_expires_at = updates.tempAccessExpiresAt || null;
      }

      // Read before writing: the Dropbox folder only has to move when the flag
      // actually flips, and after the update the old value is gone. A member we
      // have not loaded yields undefined, which counts as changed — a redundant
      // sync is a no-op, a skipped one leaves the folder in the wrong bucket.
      const previouslyActive = users.find(u => normalizeId(u.id) === normalizeId(idValue))?.isActive;

      const { data, error } = await supabase
        .from('users')
        .update(dbUpdates)
        .eq('id', idValue)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        throw new Error(`User not found (no rows updated) for id=${idValue}`);
      }

      // Active/non-active decides which half of the Dropbox portraits tree the
      // member's folder lives in, so the folder follows the flag. Fired here
      // rather than at the call sites because this is the only place is_active
      // is written — the team-member form and the fullscreen profile both come
      // through here, and neither has to know about Dropbox.
      if (dbUpdates.is_active !== undefined && dbUpdates.is_active !== previouslyActive) {
        syncTeamPortraitFolder(idValue);
      }

      await fetchUsers();
      return { success: true, data };
    } catch (err) {
      console.error('Error updating user:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'user' }) };
    }
  };

  const deleteUser = async (userId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Soft-delete: set deleted_at instead of removing the row
      const { error } = await supabase
        .from('users')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) throw error;
      await fetchUsers();
      return { success: true };
    } catch (err) {
      console.error('Error deleting user:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'user' }) };
    }
  };

  return { users, loading, createUser, updateUser, deleteUser, refetch: fetchUsers };
};

// ============================================================================
// 2. CLIENTS HOOK (3 fields)
// ============================================================================
export const useSupabaseClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      // Map from snake_case (DB) to camelCase (App)
      const mapped = (data || []).map(c => ({
        id: c.id,
        name: c.name,
        email: c.email,                                       // legacy field (kept for backward compat)
        // New fields
        organisation: c.organisation ?? '',
        linkedinUrl: c.linkedin_url ?? '',
        instagramUrl: c.instagram_url ?? '',
        address: c.address ?? '',
        mobile: c.mobile ?? '',
        whatsapp: c.whatsapp ?? '',
        customPhoneLabel: c.custom_phone_label ?? '',
        customPhoneNumber: c.custom_phone_number ?? '',
        primaryEmail: c.primary_email ?? c.email ?? '',        // fall back to legacy email
        secondaryEmail: c.secondary_email ?? '',
        dob: c.dob ?? '',
        anniversary: c.anniversary ?? '',
        additionalContacts: c.additional_contacts ?? [],       // JSONB array
        familyMembers: c.family_members ?? [],                 // JSONB array
        dietary: c.dietary ?? '',
        likesDislikes: c.likes_dislikes ?? '',
        invoiceType: c.invoice_type ?? '',
        invoiceAddress: c.invoice_address ?? '',
        invoicingName: c.invoicing_name ?? '',
        gst: c.gst ?? '',
        pan: c.pan ?? '',
        sacCode: c.sac_code ?? '',
        placeOfSupply: c.place_of_supply ?? '',
        vatBtw: c.vat_btw ?? '',
        paymentTermsDays: c.payment_terms_days != null ? Number(c.payment_terms_days) : null,
        defaultTaxRegime:                 c.default_tax_regime ?? null,
        requiresProformaInvoice:          c.requires_proforma_invoice ?? null,
        plannedDateTriggerThresholdDays:  c.planned_date_trigger_threshold_days ?? null,
        contactPreferences: c.contact_preferences ?? {},           // per-event email overrides
        // (#2) Inquiry→Client separation tracking
        origin: c.origin ?? 'manual',
        isConverted: c.is_converted ?? true,
        inquiryId: c.inquiry_id ?? null,
        // (#3) Client type & display
        clientType: c.client_type ?? 'org',
        familyName: c.family_name ?? '',
        orgLegalName: c.org_legal_name ?? '',
        displayName: c.display_name ?? c.name ?? '',
        primaryContactPersonId: c.primary_contact_person_id ?? null,
      }));
      
      setClients(mapped);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const createClient = async (clientData) => {
    try {
      await requireSession();

      // Accept both camelCase (App convention) and snake_case (legacy/direct DB names).
      // camelCase takes priority; snake_case is fallback for backward compat.
      const d = clientData;
      const dbData = {
        name: d.name,
        email: d.primaryEmail || d.primary_email || d.email || '',  // legacy email column
        organisation: d.organisation || '',
        linkedin_url: d.linkedinUrl || d.linkedin_url || '',
        instagram_url: d.instagramUrl || d.instagram_url || '',
        address: d.address || '',
        mobile: d.mobile || '',
        whatsapp: d.whatsapp || '',
        custom_phone_label: d.customPhoneLabel || d.custom_phone_label || '',
        custom_phone_number: d.customPhoneNumber || d.custom_phone_number || '',
        primary_email: d.primaryEmail || d.primary_email || '',
        secondary_email: d.secondaryEmail || d.secondary_email || '',
        dob: d.dob || null,
        anniversary: d.anniversary || null,
        additional_contacts: d.additionalContacts || d.additional_contacts || [],
        family_members: d.familyMembers || d.family_members || [],
        dietary: d.dietary || null,
        likes_dislikes: d.likesDislikes || d.likes_dislikes || null,
        invoice_type: d.invoiceType || d.invoice_type || null,
        invoice_address: d.invoiceAddress || d.invoice_address || null,
        invoicing_name: d.invoicingName || d.invoicing_name || null,
        gst: d.gst || null,
        pan: d.pan || null,
        sac_code: d.sacCode || d.sac_code || null,
        place_of_supply: d.placeOfSupply || d.place_of_supply || null,
        vat_btw: d.vatBtw || d.vat_btw || null,
        payment_terms_days: d.paymentTermsDays != null ? Number(d.paymentTermsDays) : (d.payment_terms_days != null ? Number(d.payment_terms_days) : null),
        ...(d.defaultTaxRegime !== undefined ? { default_tax_regime: d.defaultTaxRegime || null } : {}),
        ...(d.requiresProformaInvoice !== undefined ? { requires_proforma_invoice: d.requiresProformaInvoice } : {}),
        ...(d.plannedDateTriggerThresholdDays !== undefined ? { planned_date_trigger_threshold_days: d.plannedDateTriggerThresholdDays != null ? (parseInt(d.plannedDateTriggerThresholdDays) || null) : null } : {}),
        contact_preferences: d.contactPreferences || d.contact_preferences || {},
        // (#2) Inquiry→Client separation
        origin: d.origin || 'manual',
        is_converted: d.isConverted !== undefined ? d.isConverted
          : d.is_converted !== undefined ? d.is_converted : true,
        inquiry_id: d.inquiryId ?? d.inquiry_id ?? d.source_inquiry_id ?? null,
        // (#3) Client type & display
        client_type: d.clientType ?? d.client_type ?? 'org',
        family_name: d.familyName ?? d.family_name ?? null,
        org_legal_name: d.orgLegalName ?? d.org_legal_name ?? null,
        display_name: d.displayName ?? d.display_name ?? null,
      };

      const { data, error } = await supabase
        .from('clients')
        .insert([dbData])
        .select()
        .single();
      
      if (error) throw error;
      await fetchClients();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating client:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'client' }) };
    }
  };

  const updateClient = async (clientId, clientData) => {
    try {
      await requireSession();
      const d = clientData;

      // Build updates conditionally — only include fields that were provided.
      // Accepts camelCase (App convention) or snake_case (legacy) — camelCase wins.
      const dbUpdates = {};
      if (d.name !== undefined) dbUpdates.name = d.name;
      // email: from primaryEmail or email
      const hasPriEmail = d.primaryEmail !== undefined || d.primary_email !== undefined;
      const hasEmail = d.email !== undefined;
      if (hasPriEmail || hasEmail) {
        dbUpdates.email = d.primaryEmail || d.primary_email || d.email || '';
        dbUpdates.primary_email = d.primaryEmail || d.primary_email || '';
      }
      if (d.organisation !== undefined) dbUpdates.organisation = d.organisation || '';
      if (d.linkedinUrl !== undefined || d.linkedin_url !== undefined)
        dbUpdates.linkedin_url = d.linkedinUrl || d.linkedin_url || '';
      if (d.instagramUrl !== undefined || d.instagram_url !== undefined)
        dbUpdates.instagram_url = d.instagramUrl || d.instagram_url || '';
      if (d.address !== undefined) dbUpdates.address = d.address || '';
      if (d.mobile !== undefined) dbUpdates.mobile = d.mobile || '';
      if (d.whatsapp !== undefined) dbUpdates.whatsapp = d.whatsapp || '';
      if (d.customPhoneLabel !== undefined || d.custom_phone_label !== undefined)
        dbUpdates.custom_phone_label = d.customPhoneLabel || d.custom_phone_label || '';
      if (d.customPhoneNumber !== undefined || d.custom_phone_number !== undefined)
        dbUpdates.custom_phone_number = d.customPhoneNumber || d.custom_phone_number || '';
      if (d.secondaryEmail !== undefined || d.secondary_email !== undefined)
        dbUpdates.secondary_email = d.secondaryEmail || d.secondary_email || '';
      if (d.dob !== undefined) dbUpdates.dob = d.dob || null;
      if (d.anniversary !== undefined) dbUpdates.anniversary = d.anniversary || null;
      if (d.additionalContacts !== undefined || d.additional_contacts !== undefined)
        dbUpdates.additional_contacts = d.additionalContacts || d.additional_contacts || [];
      if (d.familyMembers !== undefined || d.family_members !== undefined)
        dbUpdates.family_members = d.familyMembers || d.family_members || [];
      if (d.dietary !== undefined) dbUpdates.dietary = d.dietary || null;
      if (d.likesDislikes !== undefined || d.likes_dislikes !== undefined)
        dbUpdates.likes_dislikes = d.likesDislikes || d.likes_dislikes || null;
      if (d.invoiceType !== undefined || d.invoice_type !== undefined)
        dbUpdates.invoice_type = d.invoiceType || d.invoice_type || null;
      if (d.invoiceAddress !== undefined || d.invoice_address !== undefined)
        dbUpdates.invoice_address = d.invoiceAddress || d.invoice_address || null;
      if (d.invoicingName !== undefined || d.invoicing_name !== undefined)
        dbUpdates.invoicing_name = d.invoicingName ?? d.invoicing_name ?? null;
      if (d.gst !== undefined)
        dbUpdates.gst = d.gst || null;
      if (d.pan !== undefined)
        dbUpdates.pan = d.pan || null;
      if (d.sacCode !== undefined || d.sac_code !== undefined)
        dbUpdates.sac_code = d.sacCode || d.sac_code || null;
      if (d.placeOfSupply !== undefined || d.place_of_supply !== undefined)
        dbUpdates.place_of_supply = d.placeOfSupply || d.place_of_supply || null;
      if (d.vatBtw !== undefined || d.vat_btw !== undefined)
        dbUpdates.vat_btw = d.vatBtw || d.vat_btw || null;
      if (d.paymentTermsDays !== undefined || d.payment_terms_days !== undefined)
        dbUpdates.payment_terms_days = d.paymentTermsDays != null ? Number(d.paymentTermsDays) : (d.payment_terms_days != null ? Number(d.payment_terms_days) : null);
      if (d.defaultTaxRegime !== undefined) dbUpdates.default_tax_regime = d.defaultTaxRegime || null;
      if (d.requiresProformaInvoice !== undefined) dbUpdates.requires_proforma_invoice = d.requiresProformaInvoice;
      if (d.plannedDateTriggerThresholdDays !== undefined) dbUpdates.planned_date_trigger_threshold_days = d.plannedDateTriggerThresholdDays != null ? (parseInt(d.plannedDateTriggerThresholdDays) || null) : null;
      if (d.contactPreferences !== undefined || d.contact_preferences !== undefined)
        dbUpdates.contact_preferences = d.contactPreferences || d.contact_preferences || {};
      // (#2) Inquiry→Client separation — accept both camelCase and snake_case
      if (d.origin !== undefined) dbUpdates.origin = d.origin;
      if (d.isConverted !== undefined) dbUpdates.is_converted = d.isConverted;
      else if (d.is_converted !== undefined) dbUpdates.is_converted = d.is_converted;
      if (d.inquiryId !== undefined) dbUpdates.inquiry_id = d.inquiryId;
      else if (d.inquiry_id !== undefined) dbUpdates.inquiry_id = d.inquiry_id;
      else if (d.source_inquiry_id !== undefined) dbUpdates.inquiry_id = d.source_inquiry_id;
      // (#3) Client type & display
      if (d.clientType !== undefined || d.client_type !== undefined)
        dbUpdates.client_type = d.clientType ?? d.client_type ?? null;
      if (d.familyName !== undefined || d.family_name !== undefined)
        dbUpdates.family_name = d.familyName ?? d.family_name ?? null;
      if (d.orgLegalName !== undefined || d.org_legal_name !== undefined)
        dbUpdates.org_legal_name = d.orgLegalName ?? d.org_legal_name ?? null;
      if (d.displayName !== undefined || d.display_name !== undefined)
        dbUpdates.display_name = d.displayName ?? d.display_name ?? null;
      if (d.primaryContactPersonId !== undefined || d.primary_contact_person_id !== undefined)
        dbUpdates.primary_contact_person_id = d.primaryContactPersonId ?? d.primary_contact_person_id ?? null;

      if (Object.keys(dbUpdates).length === 0) return { success: false, error: 'No updates' };

      const { data, error } = await supabase
        .from('clients')
        .update(dbUpdates)
        .eq('id', clientId)
        .select()
        .single();
      
      if (error) throw error;
      await fetchClients();
      return { success: true, data };
    } catch (err) {
      console.error('Error updating client:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'client' }) };
    }
  };

  const deleteClient = async (clientId) => {
    try {
      await requireSession();

      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);
      
      if (error) throw error;
      await fetchClients();
      return { success: true };
    } catch (err) {
      console.error('Error deleting client:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'client' }) };
    }
  };

  return { clients, loading, createClient, updateClient, deleteClient, refetch: fetchClients };
};

// ============================================================================
// 3. PROJECTS HOOK (15 fields)
// ============================================================================
export const useSupabaseProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map from snake_case (DB) to camelCase (App) — shared by fetch and update
  const mapProject = (p) => ({
    id: normalizeId(p.id),
    name: p.name,
    projectCode: p.project_code,
    clientId: normalizeId(p.client_id),
    category: p.category,
    status: p.status,
    color: p.color,
    budget: p.budget,
    projectSize: p.project_size,
    plotArea: p.plot_area ?? null,
    targetConstructionBudget: p.target_construction_budget,
    finalConstructionBudget: p.final_construction_budget,
    supervisors: normalizeIdArray(p.supervisors || []),
    budgetAllocation: p.budget_allocation,
    stageTimeline: p.stage_timeline,
    retainerConfig: p.retainer_config,
    plannedHours: p.planned_hours,
    projectAddress: p.project_address ?? '',
    city: p.city ?? '',
    country: p.country ?? '',
    invoiceProjectName: p.invoice_project_name ?? '',
    miscFeeScopes: p.misc_fee_scopes ?? [],
    contractCurrency: p.contract_currency ?? 'INR',
    projectEmailId: p.project_email_id ?? '',
    requiresProformaInvoice: p.requires_proforma_invoice ?? true,
    plannedDateTriggerThresholdDays: p.planned_date_trigger_threshold_days ?? null,
    updatedAt: p.updated_at ?? null,
    commsEnabled: p.comms_enabled ?? false,
    contractingEntity: p.contracting_entity ?? 'SBPL',               // SBPL | ABVOF, read by vof_hours_statement
    // Site details (physical setting of the project's site)
    elevationM: p.elevation_m ?? null,
    elevationSource: p.elevation_source ?? null,
    climateZone: p.climate_zone ?? '',
    siteTypes: Array.isArray(p.site_types) ? p.site_types : [],
    climateNormals: p.climate_normals ?? null,
    subsurface: p.subsurface ?? null,
    subsurfaceOverride: p.subsurface_override ?? null,
  });

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, project_code, client_id, category, status, color, budget, project_size, plot_area, target_construction_budget, final_construction_budget, supervisors, budget_allocation, stage_timeline, retainer_config, planned_hours, project_address, city, country, invoice_project_name, misc_fee_scopes, contract_currency, project_email_id, requires_proforma_invoice, planned_date_trigger_threshold_days, updated_at, comms_enabled, contracting_entity, elevation_m, elevation_source, climate_zone, site_types, climate_normals, subsurface, subsurface_override')
        .order('name');

      if (error) throw error;
      setProjects((data || []).map(mapProject));
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();

    // Realtime subscription: apply delta updates from payload instead of
    // refetching the entire projects table for every change.
    const channel = supabase
      .channel('projects-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, (payload) => {
        if (payload.eventType === 'UPDATE' && payload.new) {
          const updated = mapProject(payload.new);
          setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
        } else if (payload.eventType === 'INSERT' && payload.new) {
          setProjects(prev => [...prev, mapProject(payload.new)]);
        } else if (payload.eventType === 'DELETE' && payload.old) {
          const deletedId = normalizeId(payload.old.id);
          setProjects(prev => prev.filter(p => p.id !== deletedId));
        } else {
          fetchProjects();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const createProject = async (projectData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Map fields from camelCase (App) to snake_case (DB)
      // NOTE: id is NOT sent - let the database generate it (BIGINT GENERATED)
      const dbData = {
        name: projectData.name,                                         // required
        project_code: projectData.projectCode ?? '',                    // optional, default: ''
        client_id: projectData.clientId ?? null,                        // optional, nullable
        category: projectData.category ?? '',                           // optional, default: ''
        status: projectData.status ?? 'active',                         // optional, default: 'active'
        color: projectData.color ?? '#3b82f6',                          // optional, default: '#3b82f6'
        budget: projectData.budget ?? 0,                                // optional, default: 0
        project_size: projectData.projectSize ?? 0,                     // optional, default: 0
        plot_area: projectData.plotArea ?? null,                         // optional, land/site area in m²
        target_construction_budget: projectData.targetConstructionBudget ?? 0, // optional
        final_construction_budget: projectData.finalConstructionBudget ?? 0,   // optional
        supervisors: projectData.supervisors ?? [],                     // optional, default: []
        budget_allocation: projectData.budgetAllocation ?? {},          // optional, default: {}
        stage_timeline: projectData.stageTimeline ?? {},                // optional, default: {}
        retainer_config: projectData.retainerConfig ?? {                // optional, default object
          monthlyAmount: 0,
          startDate: null,
          totalMonths: 12,
          status: 'inactive'
        },
        planned_hours: projectData.plannedHours ?? 0,                   // optional, for project planning
        project_address: projectData.projectAddress ?? '',              // optional, street address or coordinates
        city: projectData.city ?? '',                                   // optional, readable place name for filenames
        country: projectData.country ?? '',                             // optional, pairs with city
        invoice_project_name: projectData.invoiceProjectName ?? '',     // optional, override name on invoices
        misc_fee_scopes: projectData.miscFeeScopes ?? [],              // optional, jsonb array of {id, name, amount}
        contract_currency: projectData.contractCurrency ?? 'INR',     // optional, EUR or INR
        project_email_id: projectData.projectEmailId ?? '',          // optional, email identity for project comms
        requires_proforma_invoice: projectData.requiresProformaInvoice ?? true, // optional, proforma workflow flag
        planned_date_trigger_threshold_days: projectData.plannedDateTriggerThresholdDays ?? null, // optional, threshold for planned date email
        elevation_m: projectData.elevationM ?? null,                    // optional, site elevation in metres
        elevation_source: projectData.elevationSource ?? null,          // optional, 'google' | 'manual'
        climate_zone: projectData.climateZone ?? null,                  // optional, Köppen–Geiger code
        site_types: projectData.siteTypes ?? [],                        // optional, text[] site descriptors
        climate_normals: projectData.climateNormals ?? null,            // optional, jsonb seasonal climate normals
        subsurface: projectData.subsurface ?? null,                     // optional, jsonb regional soil/bedrock context
        subsurface_override: projectData.subsurfaceOverride ?? null,    // optional, jsonb human override of the above
      };

      const { data, error } = await supabase
        .from('projects')
        .insert([dbData])
        .select()
        .single();
      
      if (error) throw error;
      await fetchProjects();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating project:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'project' }) };
    }
  };

  const updateProject = async (projectId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Guard against accidental undefined/null IDs
      if (projectId === undefined || projectId === null || projectId === '') {
        throw new Error('updateProject called without a valid projectId');
      }

      // If IDs are numeric in your DB but strings in UI, coerce safely
      const idValue = (typeof projectId === 'string' && /^\d+$/.test(projectId)) ? Number(projectId) : projectId;

      // Optional optimistic-lock timestamp: caller passes the project.updatedAt they
      // loaded with, and we add a WHERE updated_at = <that> to the query. If another
      // session saved in the meantime the row won't match and we return isStaleWrite.
      const expectedUpdatedAt = updates.expectedUpdatedAt;

      // Map from camelCase (App) to snake_case (DB)
      // Include ALL fields that can be updated (matching createProject fields)
      const dbUpdates = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.projectCode !== undefined) dbUpdates.project_code = updates.projectCode;
      if (updates.clientId !== undefined) dbUpdates.client_id = updates.clientId;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.budget !== undefined) dbUpdates.budget = updates.budget;
      if (updates.projectSize !== undefined) dbUpdates.project_size = updates.projectSize;
      if (updates.plotArea !== undefined) dbUpdates.plot_area = updates.plotArea ?? null;
      if (updates.targetConstructionBudget !== undefined) dbUpdates.target_construction_budget = updates.targetConstructionBudget;
      if (updates.finalConstructionBudget !== undefined) dbUpdates.final_construction_budget = updates.finalConstructionBudget;
      if (updates.supervisors !== undefined) dbUpdates.supervisors = updates.supervisors;
      if (updates.budgetAllocation !== undefined) dbUpdates.budget_allocation = updates.budgetAllocation;
      if (updates.stageTimeline !== undefined) dbUpdates.stage_timeline = updates.stageTimeline;
      if (updates.retainerConfig !== undefined) dbUpdates.retainer_config = updates.retainerConfig;
      if (updates.plannedHours !== undefined) dbUpdates.planned_hours = updates.plannedHours;
      if (updates.projectAddress !== undefined) dbUpdates.project_address = updates.projectAddress;
      if (updates.city !== undefined) dbUpdates.city = updates.city;
      if (updates.country !== undefined) dbUpdates.country = updates.country;
      if (updates.invoiceProjectName !== undefined) dbUpdates.invoice_project_name = updates.invoiceProjectName;
      if (updates.miscFeeScopes !== undefined) dbUpdates.misc_fee_scopes = updates.miscFeeScopes;
      if (updates.contractCurrency !== undefined) dbUpdates.contract_currency = updates.contractCurrency;
      if (updates.projectEmailId !== undefined) dbUpdates.project_email_id = updates.projectEmailId;
      if (updates.requiresProformaInvoice !== undefined) dbUpdates.requires_proforma_invoice = updates.requiresProformaInvoice;
      if (updates.plannedDateTriggerThresholdDays !== undefined) dbUpdates.planned_date_trigger_threshold_days = updates.plannedDateTriggerThresholdDays;
      if (updates.commsEnabled !== undefined) dbUpdates.comms_enabled = updates.commsEnabled;
      if (updates.contractingEntity !== undefined) dbUpdates.contracting_entity = updates.contractingEntity;
      // Site details
      if (updates.elevationM !== undefined) dbUpdates.elevation_m = updates.elevationM ?? null;
      if (updates.elevationSource !== undefined) dbUpdates.elevation_source = updates.elevationSource ?? null;
      if (updates.climateZone !== undefined) dbUpdates.climate_zone = updates.climateZone ?? null;
      if (updates.siteTypes !== undefined) dbUpdates.site_types = updates.siteTypes ?? [];
      if (updates.climateNormals !== undefined) dbUpdates.climate_normals = updates.climateNormals ?? null;
      if (updates.subsurface !== undefined) dbUpdates.subsurface = updates.subsurface ?? null;
      if (updates.subsurfaceOverride !== undefined) dbUpdates.subsurface_override = updates.subsurfaceOverride ?? null;
      // Inquiry ↔ Project linkage
      if (updates.sourceInquiryId !== undefined) dbUpdates.source_inquiry_id = updates.sourceInquiryId;

      if (Object.keys(dbUpdates).length === 0) {
        console.warn('[PROJECTS] No valid updates provided');
        return { success: false, error: 'No updates provided' };
      }
      
      let query = supabase
        .from('projects')
        .update(dbUpdates)
        .eq('id', idValue);

      if (expectedUpdatedAt) {
        query = query.eq('updated_at', expectedUpdatedAt);
      }

      const { data, error } = await query.select().maybeSingle();

      if (error) {
        console.error('[PROJECTS] Supabase update error:', error);
        throw error;
      }
      if (!data) {
        if (expectedUpdatedAt) {
          // Row exists but updated_at didn't match — another session saved first.
          return {
            success: false,
            error: 'This project was saved by another session while you were editing. Please refresh the page and re-enter your changes.',
            isStaleWrite: true,
          };
        }
        console.error('[PROJECTS] No rows updated for id:', idValue);
        throw new Error(`Project not found (no rows updated) for id=${idValue}`);
      }

      // Update local state with returned data instead of refetching all projects.
      // fetchProjects() caused race conditions when multiple updates overlapped:
      // the fetch from update A could overwrite the optimistic state of update B.
      const updatedProject = mapProject(data);
      setProjects(prev => prev.map(p =>
        p.id === updatedProject.id ? updatedProject : p
      ));
      return { success: true, data: updatedProject };
    } catch (err) {
      console.error('[PROJECTS] Error updating project:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'project' }) };
    }
  };

  const deleteProject = async (projectId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);
      
      if (error) throw error;
      await fetchProjects();
      return { success: true };
    } catch (err) {
      console.error('Error deleting project:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'project' }) };
    }
  };

  // `base` (optional) is the stage_timeline copy the caller built `partialTimeline` from. With it, only
  // the fields that differ between base and the partial are written, onto a freshly read timeline, the
  // same way updateProjectTimelineDiff does. Without it, each key of the partial replaces the stored
  // entry whole, so a tab holding an older copy writes that copy's stale dates back over the whole
  // stage. Tracking fields are allowed: the callers that pass a base are the Tracking and Invoices
  // pages, which own them.
  const mergeProjectTimeline = async (projectId, partialTimeline, base = null) => {
    if (base && typeof base === 'object') {
      if (!partialTimeline || Object.keys(partialTimeline).length === 0) return { success: true };
      return updateProjectTimelineDiff(projectId, base, { ...base, ...partialTimeline }, { allowTracking: true });
    }
    try {
      await requireSession();
      if (!projectId || !partialTimeline || Object.keys(partialTimeline).length === 0) {
        return { success: true };
      }
      const idValue = (typeof projectId === 'string' && /^\d+$/.test(projectId)) ? Number(projectId) : projectId;

      const { data, error } = await supabase
        .rpc('merge_project_stage_timeline', {
          p_project_id:        idValue,
          p_partial_timeline:  partialTimeline,
        });

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(`Project not found for id=${idValue}`);
      }

      const updatedProject = mapProject(data[0]);
      setProjects(prev => prev.map(p => p.id === updatedProject.id ? updatedProject : p));
      return { success: true, data: updatedProject };
    } catch (err) {
      console.error('[PROJECTS] Error merging stage timeline:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'project' }) };
    }
  };

  // Save a stage_timeline edit without writing back fields the caller never changed. The caller
  // passes the copy it built the edit from (base) and the result (next); only the base -> next
  // difference is applied, onto a freshly read stage_timeline, so a Tracking-page date saved
  // after the caller loaded its copy survives. Unless the caller passes allowTracking: true (the
  // Timelines page does only while "Sync to tracking" is on), no Tracking-owned field changes at
  // all. See src/utils/timelineDiff.js.
  const updateProjectTimelineDiff = (projectId, baseTimeline, nextTimeline, opts = {}) => {
    // Each save reads the stored timeline and then writes it, so two overlapping saves from this tab
    // (the Timelines page fires several on load) would drop each other's change. Queue them per project.
    const key = String(projectId);
    const prev = timelineDiffQueue.get(key) || Promise.resolve();
    const run = prev.then(() => runTimelineDiff(projectId, baseTimeline, nextTimeline, opts));
    timelineDiffQueue.set(key, run.catch(() => {}));
    return run;
  };

  const runTimelineDiff = async (projectId, baseTimeline, nextTimeline, { allowTracking = false, granted = false } = {}) => {
    try {
      await requireSession();
      const idValue = (typeof projectId === 'string' && /^\d+$/.test(projectId)) ? Number(projectId) : projectId;
      const { data, error } = await supabase
        .from('projects')
        .select('stage_timeline')
        .eq('id', idValue)
        .single();
      if (error) throw error;
      const fresh = data?.stage_timeline || {};
      let merged = applyTimelineDiff(baseTimeline, nextTimeline, fresh);
      if (!allowTracking) merged = protectTrackingFields(merged, fresh);
      if (granted) return saveGrantedTimeline(idValue, merged);
      return updateProject(projectId, { stageTimeline: merged });
    } catch (err) {
      console.error('[PROJECTS] Error saving stage timeline diff:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'project' }) };
    }
  };

  // A user holding a Full grant on a timeline (not an admin, not the project's supervisor) cannot
  // UPDATE projects under RLS. Their saves go through save_granted_project_timeline, which checks the
  // stored grant and writes only what it covers (task fields, schedule fields, their own rows), keeping
  // the stored value of everything else. The returned row is the truth: anything it dropped reverts.
  const saveGrantedTimeline = async (idValue, merged) => {
    const { data, error } = await supabase.rpc('save_granted_project_timeline', {
      p_project_id: idValue,
      p_timeline: merged,
    });
    if (error) {
      console.error('[PROJECTS] Granted timeline save rejected:', error);
      return { success: false, error: error.message || formatSupabaseError(error, { action: 'update', entity: 'project' }) };
    }
    const updatedProject = mapProject(data);
    setProjects(prev => prev.map(p => (p.id === updatedProject.id ? updatedProject : p)));
    return { success: true, data: updatedProject };
  };

  // The Timelines "Give access" map (stage_timeline._grantedAccess). Saved through
  // save_project_timeline_grants, which writes that key only, so a user on the
  // can_grant_timeline_access() list can give access on projects they cannot otherwise UPDATE.
  const saveTimelineGrants = async (projectId, grants) => {
    try {
      await requireSession();
      const idValue = (typeof projectId === 'string' && /^\d+$/.test(projectId)) ? Number(projectId) : projectId;
      const { data, error } = await supabase.rpc('save_project_timeline_grants', {
        p_project_id: idValue,
        p_grants: grants ?? null,
      });
      if (error) throw error;
      const updatedProject = mapProject(data);
      setProjects(prev => prev.map(p => (p.id === updatedProject.id ? updatedProject : p)));
      return { success: true, data: updatedProject };
    } catch (err) {
      console.error('[PROJECTS] Error saving timeline access:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'project' }) };
    }
  };

  return { projects, loading, createProject, updateProject, deleteProject, mergeProjectTimeline, updateProjectTimelineDiff, saveTimelineGrants, refetch: fetchProjects };
};

// ============================================================================
// 4. TASKS HOOK (3 fields: id, name, order)
// ============================================================================
export const useSupabaseTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('id');
      
      if (error) throw error;
      
      // Map from snake_case (DB) to camelCase (App)
      const mapped = (data || []).map(t => ({
        id: t.id,                               // required
        name: t.name,                           // required
        order: t.order != null ? t.order : null // optional (stage ordering)
      }));
      
      setTasks(mapped);
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, []);

  const createTask = async (taskData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Map fields from camelCase (App) to snake_case (DB)
      // NOTE: id is NOT sent - let the database generate it (BIGINT GENERATED)
      const dbData = {
        name: taskData.name                     // required
      };

      const { data, error } = await supabase
        .from('tasks')
        .insert([dbData])
        .select()
        .single();
      
      if (error) throw error;
      await fetchTasks();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating task:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'task' }) };
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Map from camelCase (App) to snake_case (DB)
      const dbData = {};
      if (updates.name !== undefined) dbData.name = updates.name;
      if (updates.order !== undefined) dbData.order = updates.order;

      const { error } = await supabase
        .from('tasks')
        .update(dbData)
        .eq('id', taskId);
      
      if (error) throw error;
      await fetchTasks();
      return { success: true };
    } catch (err) {
      console.error('Error updating task:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'task' }) };
    }
  };

  const deleteTask = async (taskId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);
      
      if (error) throw error;
      await fetchTasks();
      return { success: true };
    } catch (err) {
      console.error('Error deleting task:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'task' }) };
    }
  };

  return { tasks, loading, createTask, updateTask, deleteTask, refetch: fetchTasks };
};

// ============================================================================
// 5. TIME ENTRIES HOOK (12 fields)
// ============================================================================

/**
 * SHARED HELPER: Normalize userId to BIGINT
 * - If UUID string (auth_user_id format), resolve via users.auth_user_id → users.id
 * - If numeric string, parseInt
 * - If already number, use as-is
 * - Throws on invalid/unresolvable
 * 
 * @param {string|number} userId - The userId to normalize
 * @param {string} context - For logging (e.g., 'create' or 'update')
 * @returns {Promise<number>} - The normalized BIGINT user id
 */
const normalizeUserIdToBigint = async (userId, context = 'operation') => {
  const originalValue = userId;
  
  // Check if userId looks like a UUID (36 chars with hyphens)
  const isUUID = typeof userId === 'string' && 
                 /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
  
  if (isUUID) {
    // Resolve UUID to bigint employee id via users.auth_user_id
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_user_id', userId)
      .maybeSingle();
    
    if (userError) {
      console.error(`[TIME_ENTRIES] [${context}] Error resolving UUID to employee id:`, userError);
      throw new Error(`Failed to resolve user UUID: ${userError.message}`);
    }
    
    if (!userData) {
      console.error(`[TIME_ENTRIES] [${context}] No employee record found for auth_user_id:`, userId);
      throw new Error(`No employee record found for auth_user_id: ${userId}`);
    }
    
    return userData.id;
  }
  
  if (typeof userId === 'string' && /^\d+$/.test(userId)) {
    // Numeric string - parse to integer
    const parsed = parseInt(userId, 10);
    return parsed;
  }
  
  if (typeof userId === 'number') {
    // Already a number, use as-is
    return userId;
  }
  
  // Invalid format
  throw new Error(`Invalid userId format: ${typeof userId} - ${userId}`);
};

export const useSupabaseTimeEntries = ({ initialScopeUserId = null } = {}) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadAllProgress, setLoadAllProgress] = useState(0); // 0–100
  // Guards against concurrent fetchAllTimeEntries invocations. Multiple callers
  // (App.js effects + page-level effects) can fire on the same render, and
  // useCallback identity changes when allLoaded flips, which would otherwise
  // start a second paging loop while the first is still running.
  const fetchAllInProgressRef = useRef(false);
  const [userLastEntryDates, setUserLastEntryDates] = useState({}); // { [userId]: 'YYYY-MM-DD' }
  const [userCurrentMonthHours, setUserCurrentMonthHours] = useState({}); // { [userId]: hoursNumber, _month: 'YYYY-MM' }
  // Initialized from the caller so the very first fetch is already scoped — avoids a
  // race condition where a global fetch (scopeUserId=null) and a scoped fetch both
  // run concurrently and the global one overwrites scoped results if it lands later.
  const [scopeUserId, setScopeUserId] = useState(
    (initialScopeUserId && Number.isFinite(initialScopeUserId) && initialScopeUserId > 0)
      ? initialScopeUserId
      : null
  );

  // ============================================================================
  // KEYSET PAGINATION HELPER
  // ============================================================================
  // Maps raw DB rows (snake_case) to app format (camelCase)
  const mapTimeEntry = (e) => ({
    id: e.id,                                       // required
    userId: e.user_id,                              // required
    projectId: e.project_id,                        // required
    taskId: e.task_id,                              // required
    description: e.description,                     // optional
    duration: e.duration,                           // required
    date: e.date,                                   // required
    billable: e.billable,                           // optional
    notes: e.notes,                                 // optional
    approved: e.approved,                           // optional
    msCalendarEventId: e.ms_calendar_event_id,      // optional, nullable
    msCalendarSynced: e.ms_calendar_synced,         // optional, nullable
    syncedToMs: e.synced_to_ms                      // optional, nullable
  });

  // ============================================================================
  // FETCH TIME ENTRIES (with keyset pagination)
  // ============================================================================
  // @param {Object} options
  // @param {number} options.limit - Number of entries to fetch (default: 500)
  // @param {number} options.beforeId - Fetch entries with id < beforeId (keyset cursor)
  // @returns {Promise<{data: Array, hasMore: boolean}>}
  const fetchTimeEntries = useCallback(async ({ limit = 200, beforeId = null } = {}) => {
    const isInitialLoad = beforeId === null;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let query = supabase
        .from('time_entries')
        .select('id, user_id, project_id, task_id, description, duration, date, billable, notes, approved, ms_calendar_event_id, ms_calendar_synced, synced_to_ms')
        .order('date', { ascending: false })
        .order('id', { ascending: false })
        .limit(limit);

      // Server-side scope: only fetch the logged-in user's own entries when scope is active
      if (scopeUserId !== null) {
        query = query.eq('user_id', scopeUserId);
      }

      // Keyset pagination: fetch entries with id < beforeId
      if (beforeId !== null) {
        query = query.lt('id', beforeId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const pageData = data || [];
      const mapped = pageData.map(mapTimeEntry);
      
      // Determine if there are more entries to load
      const pageHasMore = pageData.length === limit;
      setHasMore(pageHasMore);

      if (isInitialLoad) {
        // Initial load: replace all entries
        setTimeEntries(mapped);
      } else {
        // Subsequent load: append entries
        setTimeEntries(prev => [...prev, ...mapped]);
      }

      return { data: mapped, hasMore: pageHasMore };
    } catch (err) {
      console.error('Error fetching time entries:', err);
      return { data: [], hasMore: false };
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [scopeUserId]);

  // ============================================================================
  // LOAD MORE TIME ENTRIES
  // ============================================================================
  // Convenience wrapper for loading more entries using the last loaded entry's ID
  // @param {number} beforeId - The ID to use as cursor (id < beforeId)
  // @param {number} limit - Number of entries to fetch (default: 500)
  const loadMoreTimeEntries = useCallback(async (beforeId, limit = 500) => {
    if (!beforeId || loadingMore) return { data: [], hasMore: false };
    return fetchTimeEntries({ limit, beforeId });
  }, [fetchTimeEntries, loadingMore]);

  // ============================================================================
  // FETCH ALL TIME ENTRIES (paginated, for cost ledger)
  // ============================================================================
  // Loads ALL time entries by paging through the entire table.
  // Only runs once — subsequent calls are no-ops if allLoaded is true.
  const fetchAllTimeEntries = useCallback(async () => {
    if (allLoaded) return;
    if (fetchAllInProgressRef.current) return;
    fetchAllInProgressRef.current = true;
    setLoading(true);
    setLoadAllProgress(0);
    try {
      const PAGE_SIZE = 1000;
      let allData = [];
      let lastId = null;
      let pageHasMore = true;
      let pageNum = 0;

      while (pageHasMore) {
        let query = supabase
          .from('time_entries')
          .select('id, user_id, project_id, task_id, description, duration, date, billable, notes, approved, ms_calendar_event_id, ms_calendar_synced, synced_to_ms')
          .order('id', { ascending: false })
          .limit(PAGE_SIZE);

        if (lastId !== null) {
          query = query.lt('id', lastId);
        }

        const { data, error } = await query;
        if (error) throw error;

        const page = data || [];
        allData = allData.concat(page);
        pageHasMore = page.length === PAGE_SIZE;
        pageNum++;
        if (page.length > 0) {
          lastId = page[page.length - 1].id;
        }

        // Progress: asymptotic estimate based on pages loaded
        // (50% after page 1, 67% after 2, 75% after 3, …)
        if (pageHasMore) {
          setLoadAllProgress(Math.min(99, Math.round(100 * (1 - 1 / (pageNum + 1)))));
        } else {
          setLoadAllProgress(99);
        }
      }

      const mapped = allData.map(mapTimeEntry);
      setTimeEntries(mapped);
      setHasMore(false);
      setLoadAllProgress(100);
      setAllLoaded(true);
      console.log(`[TIME_ENTRIES] Loaded all ${mapped.length} entries`);
    } catch (err) {
      console.error('[TIME_ENTRIES] Error fetching all time entries:', err);
      setAllLoaded(true);
    } finally {
      setLoading(false);
      fetchAllInProgressRef.current = false;
    }
  }, [allLoaded]);

  // Initial fetch on mount (and whenever scopeUserId changes, since fetchTimeEntries depends on it)
  useEffect(() => { fetchTimeEntries(); }, [fetchTimeEntries]);

  // Real-time subscription: when scoped to a user, listen for INSERT/UPDATE/DELETE on their rows.
  // Applies targeted state updates so the time log stays current without a full refetch round-trip.
  useEffect(() => {
    if (scopeUserId === null) return;

    const channel = supabase
      .channel(`time-entries-user-${scopeUserId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'time_entries', filter: `user_id=eq.${scopeUserId}` },
        (payload) => {
          if (payload.eventType === 'INSERT' && payload.new) {
            const incoming = mapTimeEntry(payload.new);
            setTimeEntries(prev => {
              // Skip if optimistic insert already landed
              if (prev.some(e => String(e.id) === String(incoming.id))) return prev;
              return [incoming, ...prev].sort((a, b) =>
                b.date !== a.date ? b.date.localeCompare(a.date) : b.id - a.id
              );
            });
          } else if (payload.eventType === 'UPDATE' && payload.new) {
            const updated = mapTimeEntry(payload.new);
            setTimeEntries(prev => prev.map(e => String(e.id) === String(updated.id) ? updated : e));
          } else if (payload.eventType === 'DELETE' && payload.old) {
            const deletedId = payload.old.id;
            setTimeEntries(prev => prev.filter(e => String(e.id) !== String(deletedId)));
          } else {
            fetchTimeEntries();
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [scopeUserId, fetchTimeEntries]);

  // Fetch all entries for a specific user over the last N days and merge into state.
  // Used by the donut tracker to ensure a user's own recent entries are always loaded
  // regardless of the global pagination window (which covers the whole team and may
  // exclude a user who hasn't logged in a while).
  const fetchEntriesForUser = useCallback(async (userId, days = 60) => {
    try {
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - days);
      const dateFromStr = dateFrom.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('time_entries')
        .select('id, user_id, project_id, task_id, description, duration, date, billable, notes, approved, ms_calendar_event_id, ms_calendar_synced, synced_to_ms')
        .eq('user_id', userId)
        .gte('date', dateFromStr)
        .order('date', { ascending: false });

      if (error) throw error;

      const mapped = (data || []).map(mapTimeEntry);

      // Merge into existing state without replacing it — deduplicate by ID so
      // entries already present from the initial paginated fetch are not duplicated.
      setTimeEntries(prev => {
        const existingIds = new Set(prev.map(e => String(e.id)));
        const fresh = mapped.filter(e => !existingIds.has(String(e.id)));
        return fresh.length === 0 ? prev : [...prev, ...fresh];
      });

      return { success: true, count: mapped.length };
    } catch (err) {
      console.error('[TIME_ENTRIES] fetchEntriesForUser error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Sets the server-side scope to a specific user's entries.
  // Called from App.js after auth resolves for every role — time log shows only own entries.
  // Resets allLoaded so admin views can re-trigger fetchAllTimeEntries when needed.
  const initializeScope = useCallback((userId) => {
    if (!userId || !Number.isFinite(userId) || userId <= 0) return;
    setScopeUserId(prev => {
      if (prev === userId) return prev; // idempotent — skip if already scoped to same user
      return userId;
    });
    setAllLoaded(false);
    // fetchTimeEntries re-fires automatically via its useEffect because its identity
    // changes when scopeUserId changes (scopeUserId is in the useCallback dep array)
  }, []);

  // Resets allLoaded and re-fetches scoped data. Used when returning from an admin
  // view (e.g. TimesheetOverview) that replaced timeEntries state with global data via
  // fetchAllTimeEntries — the scope is already set so initializeScope won't re-fetch
  // (idempotent), so we need an explicit refetch here.
  const refetchScoped = useCallback(async () => {
    setAllLoaded(false);
    return fetchTimeEntries();
  }, [fetchTimeEntries]);

  const createTimeEntry = async (entryData) => {
    try {
      // ========================================
      // VALIDATION: Required fields check
      // ========================================
      if (entryData.userId === undefined || entryData.userId === null) {
        throw new Error('createTimeEntry: userId is required');
      }
      if (entryData.projectId === undefined || entryData.projectId === null) {
        throw new Error('createTimeEntry: projectId is required');
      }
      if (entryData.duration === undefined || entryData.duration === null) {
        throw new Error('createTimeEntry: duration is required');
      }
      if (!entryData.date) {
        throw new Error('createTimeEntry: date is required');
      }

      // ========================================
      // RLS COMPLIANCE: Get session for auth_user_id
      // ========================================
      const authUserId = await requireSession();

      // ========================================
      // NORMALIZE userId: UUID → bigint employee ID
      // Uses shared helper to ensure consistency
      // ========================================
      const normalizedUserId = await normalizeUserIdToBigint(entryData.userId, 'create');

      // ========================================
      // Map camelCase (App) to snake_case (DB)
      // DO NOT include 'id' - let Supabase/DB generate it
      // DO NOT include UI-only fields like hours, minutes, hoursText
      // ========================================
      const dbData = {
        user_id: normalizedUserId,                              // required (employee bigint)
        project_id: entryData.projectId,                        // required
        task_id: entryData.taskId ?? null,                      // optional, nullable
        description: entryData.description ?? '',               // optional, default: ''
        duration: entryData.duration,                           // required
        date: entryData.date,                                   // required
        billable: entryData.billable ?? true,                   // optional, default: true
        notes: entryData.notes ?? '',                           // optional, default: ''
        approved: entryData.approved ?? false,                  // optional, default: false
        ms_calendar_event_id: entryData.msCalendarEventId ?? null,
        ms_calendar_synced: entryData.msCalendarSynced ?? null,
        synced_to_ms: entryData.syncedToMs ?? null,
        auth_user_id: authUserId                                // RLS REQUIRED
      };

      const { data, error } = await supabase
        .from('time_entries')
        .insert([dbData])
        .select('*')
        .maybeSingle();
      
      if (error) {
        console.error('[TIME_ENTRIES] Supabase insert error:', error);
        throw error;
      }

      // FIX B1: Optimistically add the inserted row to hook state immediately.
      // This ensures activeTimeEntries updates without waiting for a full refetch.
      // The next refetch() will replace all state, deduplicating this entry.
      if (data) {
        const mapped = mapTimeEntry(data);
        setTimeEntries(prev => [mapped, ...prev]);
      }

      return { success: true, data };
    } catch (err) {
      console.error('[TIME_ENTRIES] Error creating time entry:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'time entry' }) };
    }
  };

  const updateTimeEntry = async (entryId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();


      // ========================================
      // NORMALIZE userId if present: UUID → bigint
      // Prevents accidentally writing UUID into user_id column
      // ========================================
      let normalizedUserId;
      if (updates.userId !== undefined) {
        normalizedUserId = await normalizeUserIdToBigint(updates.userId, 'update');
      }

      // Map from camelCase (App) to snake_case (DB)
      // IMPORTANT: Only include actual DB columns, NOT UI-only fields
      const dbUpdates = {};
      if (normalizedUserId !== undefined) dbUpdates.user_id = normalizedUserId;
      if (updates.projectId !== undefined) dbUpdates.project_id = updates.projectId;
      if (updates.taskId !== undefined) dbUpdates.task_id = updates.taskId;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.duration !== undefined) dbUpdates.duration = updates.duration;
      if (updates.date !== undefined) dbUpdates.date = updates.date;
      if (updates.billable !== undefined) dbUpdates.billable = updates.billable;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
      if (updates.approved !== undefined) dbUpdates.approved = updates.approved;
      if (updates.approvedBy !== undefined) dbUpdates.approved_by = updates.approvedBy;
      if (updates.approvedAt !== undefined) dbUpdates.approved_at = updates.approvedAt;
      // NOTE: hours, minutes, hoursText are UI-only fields - DO NOT send to DB


      if (Object.keys(dbUpdates).length === 0) {
        console.warn('[TIME_ENTRIES] No valid DB updates provided');
        return { success: false, error: 'No updates provided' };
      }
      
      const { data, error } = await supabase
        .from('time_entries')
        .update(dbUpdates)
        .eq('id', entryId)
        .select()
        .single();
      
      if (error) {
        console.error('[TIME_ENTRIES] Supabase update error:', error);
        throw error;
      }

      return { success: true, data };
    } catch (err) {
      console.error('[TIME_ENTRIES] Error updating time entry:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'time entry' }) };
    }
  };

  const deleteTimeEntry = async (entryId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();


      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', entryId);
      
      if (error) {
        console.error('[TIME_ENTRIES] Supabase delete error:', error);
        throw error;
      }

      return { success: true };
    } catch (err) {
      console.error('[TIME_ENTRIES] Error deleting time entry:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'time entry' }) };
    }
  };

  const deleteTimeEntryNoRefetch = async (entryId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', entryId);
      
      if (error) throw error;
      return { success: true };
    } catch (err) {
      console.error('Error deleting time entry (no refetch):', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'time entry' }) };
    }
  };

  const bulkUpdateTimeEntries = async (entryIds, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();


      const dbUpdates = {};
      if (updates.billable !== undefined) dbUpdates.billable = updates.billable;
      if (updates.approved !== undefined) dbUpdates.approved = updates.approved;
      if (updates.approvedBy !== undefined) dbUpdates.approved_by = updates.approvedBy;
      if (updates.approvedAt !== undefined) dbUpdates.approved_at = updates.approvedAt;

      
      const { error } = await supabase
        .from('time_entries')
        .update(dbUpdates)
        .in('id', entryIds);
      
      if (error) {
        console.error('[TIME_ENTRIES] Supabase bulk update error:', error);
        throw error;
      }

      return { success: true };
    } catch (err) {
      console.error('[TIME_ENTRIES] Error bulk updating time entries:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'bulk update', entity: 'time entries' }) };
    }
  };

  const bulkDeleteTimeEntries = async (entryIds) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();


      const { error } = await supabase
        .from('time_entries')
        .delete()
        .in('id', entryIds);
      
      if (error) {
        console.error('[TIME_ENTRIES] Supabase bulk delete error:', error);
        throw error;
      }

      return { success: true };
    } catch (err) {
      console.error('[TIME_ENTRIES] Error bulk deleting time entries:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'bulk delete', entity: 'time entries' }) };
    }
  };

  // Fetches MAX(date) per user via DB function — lightweight alternative to
  // scanning the paginated timeEntries array. Used by the Radar.
  const fetchUserLastEntryDates = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_user_last_entry_dates');
      if (error) throw error;
      const map = {};
      for (const row of (data || [])) {
        map[row.user_id] = row.last_date;
      }
      setUserLastEntryDates(map);
    } catch (err) {
      console.error('[TIME_ENTRIES] Error fetching user last entry dates:', err);
    }
  }, []);

  // Fetches SUM(duration)/3600 per user for a given month (YYYY-MM) via DB
  // function — same rationale as fetchUserLastEntryDates. Used by the Radar's
  // hours-under-target and overwork rules. Defaults to the current month.
  const fetchUserMonthHours = useCallback(async (monthStr = null) => {
    const month = monthStr || new Date().toISOString().slice(0, 7);
    try {
      const { data, error } = await supabase.rpc('get_user_month_hours', { month_str: month });
      if (error) throw error;
      const map = { _month: month };
      for (const row of (data || [])) {
        // numeric column comes back as a string from PostgREST; coerce.
        map[row.user_id] = parseFloat(row.hours) || 0;
      }
      setUserCurrentMonthHours(map);
    } catch (err) {
      console.error('[TIME_ENTRIES] Error fetching user month hours:', err);
    }
  }, []);

  return {
    timeEntries,
    loading,
    loadingMore,
    hasMore,
    allLoaded,
    loadAllProgress,
    userLastEntryDates,
    fetchUserLastEntryDates,
    userCurrentMonthHours,
    fetchUserMonthHours,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    deleteTimeEntryNoRefetch,
    bulkUpdateTimeEntries,
    bulkDeleteTimeEntries,
    refetch: () => fetchTimeEntries(),
    refetchScoped,
    fetchAllTimeEntries,
    fetchEntriesForUser,
    initializeScope,
    loadMoreTimeEntries,
    optimisticDelete: (id) => setTimeEntries(prev => prev.filter(e => e.id !== id)),
    optimisticUpdate: (id, updates) => setTimeEntries(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e))
  };
};

// ============================================================================
// 6. HOLIDAYS HOOK (3 fields)
// ============================================================================
export const useSupabaseHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHolidays = async () => {
    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .order('date');
      
      if (error) throw error;
      
      const mapped = (data || []).map(h => ({
        id: h.id,
        name: h.name,
        date: h.date,
        office: h.office || null, // 'NL', 'IN', or null (all offices)
      }));
      
      setHolidays(mapped);
    } catch (err) {
      console.error('Error fetching holidays:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHolidays(); }, []);

  const createHoliday = async (holidayData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // NOTE: id is NOT sent - let the database generate it
      const dbData = {
        name: holidayData.name,
        date: holidayData.date,
      };
      // Only include office if set (avoids schema error if column not yet migrated)
      if (holidayData.office) dbData.office = holidayData.office;

      const { data, error } = await supabase
        .from('holidays')
        .insert([dbData])
        .select()
        .single();

      if (error) throw error;
      await fetchHolidays();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating holiday:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'holiday' }) };
    }
  };

  const updateHoliday = async (holidayId, holidayData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const dbData = {
        name: holidayData.name,
        date: holidayData.date,
      };
      // Only include office if the field is explicitly provided (null = all offices)
      if ('office' in holidayData) dbData.office = holidayData.office || null;

      const { data, error } = await supabase
        .from('holidays')
        .update(dbData)
        .eq('id', holidayId)
        .select()
        .single();

      if (error) throw error;
      await fetchHolidays();
      return { success: true, data };
    } catch (err) {
      console.error('Error updating holiday:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'holiday' }) };
    }
  };

  const deleteHoliday = async (holidayId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', holidayId);

      if (error) throw error;
      await fetchHolidays();
      return { success: true };
    } catch (err) {
      console.error('Error deleting holiday:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'holiday' }) };
    }
  };

  return { holidays, loading, createHoliday, updateHoliday, deleteHoliday, refetch: fetchHolidays };
};

// ============================================================================
// 7. EXPENSES HOOK (7 fields)
// ============================================================================
export const useSupabaseExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select('id, project_id, user_id, category, description, amount, date, status, office_location, amount_original, currency_original, fx_rate_to_inr, amount_in_inr, import_source, import_batch_id, linked_reimbursement_id, receipt_status, client_billable, billing_status, is_office_expense, voucher_id, voucher_date, voucher_status, tax_code, tax_rate, tax_amount, net_amount, gross_amount, tax_recoverable, tax_status, tax_confirmed_at, tax_source_ref, foreign_vendor')
        .order('date', { ascending: false });
      
      if (error) throw error;
      
      const mapped = (data || []).map(e => ({
        id: e.id,
        projectId: e.project_id,
        userId: e.user_id ?? null,
        category: e.category,
        description: e.description,
        amount: e.amount,
        date: e.date,
        status: e.status,
        officeLocation: e.office_location || 'NL',
        // Currency fields (fallback for pre-migration rows)
        amountOriginal:   e.amount_original   ?? e.amount,
        currencyOriginal: e.currency_original || 'INR',
        fxRateToInr:      e.fx_rate_to_inr    ?? 1,
        amountInInr:      e.amount_in_inr     ?? e.amount,
        importSource:          e.import_source || null,
        importBatchId:         e.import_batch_id || null,
        linkedReimbursementId: e.linked_reimbursement_id || null,
        receiptStatus:         e.receipt_status || 'missing',
        clientBillable:        e.client_billable ?? false,
        billingStatus:         e.billing_status || null,
        isOfficeExpense:       e.is_office_expense ?? false,
        voucherId:             e.voucher_id || null,
        voucherDate:           e.voucher_date || null,
        voucherStatus:         e.voucher_status || null,
        // Tax reconciliation fields (nullable on legacy rows)
        taxCode:               e.tax_code        ?? null,
        taxRate:               e.tax_rate        ?? null,
        taxAmount:             e.tax_amount      ?? null,
        netAmount:             e.net_amount      ?? null,
        grossAmount:           e.gross_amount    ?? null,
        taxRecoverable:        e.tax_recoverable ?? null,
        taxStatus:             e.tax_status      || null,
        taxConfirmedAt:        e.tax_confirmed_at || null,
        taxSourceRef:          e.tax_source_ref  || null,
        foreignVendor:         e.foreign_vendor  ?? false,
      }));
      
      setExpenses(mapped);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); }, []);

  const createExpense = async (expenseData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Get session for created_by
      // ========================================
      const authUserId = await requireSession();

      // Note: id is NOT sent - let the database generate it
      const dbData = {
        project_id: expenseData.projectId,
        category: expenseData.category,
        description: expenseData.description,
        amount: expenseData.amount,
        date: expenseData.date || new Date().toISOString().split('T')[0],
        status: expenseData.status ?? 'planned',
        office_location: expenseData.officeLocation || 'NL',
        created_by: authUserId,                             // UUID (auth user id)
        // Currency fields
        amount_original:   expenseData.amountOriginal   ?? expenseData.amount,
        currency_original: expenseData.currencyOriginal || 'INR',
        fx_rate_to_inr:    expenseData.fxRateToInr      ?? 1,
        amount_in_inr:     expenseData.amountInInr       ?? expenseData.amount,
        foreign_vendor:    expenseData.foreignVendor     ?? false,
      };

      const { data, error } = await supabase
        .from('expenses')
        .insert([dbData])
        .select()
        .single();
      
      if (error) {
        console.error('[EXPENSES] Supabase error:', error);
        throw error;
      }
      
      await fetchExpenses();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating expense:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'expense' }) };
    }
  };

  // Bulk create: single DB round-trip for CSV imports
  const createExpenses = async (expenseArray) => {
    try {
      await requireSession();

      const dbRows = expenseArray.map(expenseData => ({
        project_id: expenseData.projectId,
        category: expenseData.category,
        description: expenseData.description,
        amount: expenseData.amount,
        date: expenseData.date || new Date().toISOString().split('T')[0],
        status: expenseData.status ?? 'planned',
        office_location: expenseData.officeLocation || 'NL',
        amount_original:   expenseData.amountOriginal   ?? expenseData.amount,
        currency_original: expenseData.currencyOriginal || 'INR',
        fx_rate_to_inr:    expenseData.fxRateToInr      ?? 1,
        amount_in_inr:     expenseData.amountInInr       ?? expenseData.amount,
        foreign_vendor:    expenseData.foreignVendor     ?? false,
      }));

      // Skip .select() — avoids RLS COALESCE type-mismatch errors on
      // computed/default columns; we refetch the full list below anyway.
      const { error } = await supabase
        .from('expenses')
        .insert(dbRows);

      if (error) {
        console.error('[EXPENSES] Bulk insert error:', error);
        throw error;
      }

      await fetchExpenses();
      return { success: true, count: dbRows.length };
    } catch (err) {
      console.error('Error bulk creating expenses:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'expenses (bulk)' }) };
    }
  };

  const updateExpense = async (expenseId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      if (expenseId === undefined || expenseId === null || expenseId === '') {
        throw new Error('updateExpense called without a valid expenseId');
      }

      const dbUpdates = {};
      if (updates.projectId !== undefined) dbUpdates.project_id = updates.projectId;
      if (updates.userId    !== undefined) dbUpdates.user_id    = updates.userId;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
      if (updates.date !== undefined) dbUpdates.date = updates.date;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.officeLocation !== undefined) dbUpdates.office_location = updates.officeLocation;
      // Currency fields
      if (updates.amountOriginal   !== undefined) dbUpdates.amount_original   = updates.amountOriginal;
      if (updates.currencyOriginal !== undefined) dbUpdates.currency_original = updates.currencyOriginal;
      if (updates.fxRateToInr      !== undefined) dbUpdates.fx_rate_to_inr    = updates.fxRateToInr;
      if (updates.amountInInr      !== undefined) dbUpdates.amount_in_inr     = updates.amountInInr;
      if (updates.clientBillable   !== undefined) dbUpdates.client_billable   = updates.clientBillable;
      if (updates.billingStatus    !== undefined) dbUpdates.billing_status    = updates.billingStatus;
      if (updates.isOfficeExpense  !== undefined) dbUpdates.is_office_expense = updates.isOfficeExpense;
      if (updates.voucherId        !== undefined) dbUpdates.voucher_id        = updates.voucherId;
      if (updates.voucherDate      !== undefined) dbUpdates.voucher_date      = updates.voucherDate;
      if (updates.voucherStatus    !== undefined) dbUpdates.voucher_status    = updates.voucherStatus;
      // Tax reconciliation fields
      if (updates.taxCode          !== undefined) dbUpdates.tax_code          = updates.taxCode;
      if (updates.taxRate          !== undefined) dbUpdates.tax_rate          = updates.taxRate;
      if (updates.taxAmount        !== undefined) dbUpdates.tax_amount        = updates.taxAmount;
      if (updates.netAmount        !== undefined) dbUpdates.net_amount        = updates.netAmount;
      if (updates.grossAmount      !== undefined) dbUpdates.gross_amount      = updates.grossAmount;
      if (updates.taxRecoverable   !== undefined) dbUpdates.tax_recoverable   = updates.taxRecoverable;
      if (updates.taxStatus        !== undefined) dbUpdates.tax_status        = updates.taxStatus;
      if (updates.taxConfirmedAt   !== undefined) dbUpdates.tax_confirmed_at  = updates.taxConfirmedAt;
      if (updates.taxSourceRef     !== undefined) dbUpdates.tax_source_ref    = updates.taxSourceRef;
      if (updates.foreignVendor    !== undefined) dbUpdates.foreign_vendor    = updates.foreignVendor;

      const { data, error } = await supabase
        .from('expenses')
        .update(dbUpdates)
        .eq('id', expenseId)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      await fetchExpenses();
      return { success: true, data };
    } catch (err) {
      console.error('Error updating expense:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'expense' }) };
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Unlink any reimbursements that reference this expense before deleting
      const { error: unlinkError } = await supabase
        .from('reimbursements')
        .update({ linked_expense_id: null, updated_at: new Date().toISOString() })
        .eq('linked_expense_id', expenseId);
      if (unlinkError) console.warn('Could not unlink reimbursements:', unlinkError.message);

      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      console.error('Error deleting expense:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'expense' }) };
    }
  };

  /**
   * Bulk-import expenses via a single batch insert.
   * Sets created_by to the auth UUID (expenses.created_by is UUID).
   * Returns { successCount, totalCount, errors[] }.
   */
  const bulkImportExpenses = async (expenseRows) => {
    const results = { successCount: 0, totalCount: expenseRows.length, errors: [] };
    if (!expenseRows.length) return results;

    let authUserId = null;
    try {
      authUserId = await requireSession();
    } catch (err) {
      results.errors.push('Session required: ' + (err.message || String(err)));
      return results;
    }

    // Build minimal DB rows – only columns we know exist
    const dbRows = expenseRows.map((exp, idx) => {
      const row = {
        project_id:        exp.projectId != null ? Number(exp.projectId) : null,
        category:          exp.category || 'Uncategorised',
        description:       exp.description || '',
        amount:            Number(exp.amount) || 0,
        date:              exp.date || new Date().toISOString().split('T')[0],
        status:            exp.status || 'planned',
        office_location:   exp.officeLocation || 'NL',
        created_by:        authUserId,                       // UUID (auth user id)
        amount_original:   Number(exp.amountOriginal ?? exp.amount) || 0,
        currency_original: exp.currencyOriginal || 'INR',
        fx_rate_to_inr:    Number(exp.fxRateToInr ?? 1),
        amount_in_inr:     Number(exp.amountInInr ?? exp.amount) || 0,
        ...(exp.importSource ? { import_source: exp.importSource } : {}),
        ...(exp.importBatchId ? { import_batch_id: exp.importBatchId } : {}),
        ...(exp.userId ? { user_id: exp.userId } : {}),
        client_billable:    exp.clientBillable ?? false,
        ...(exp.billingStatus ? { billing_status: exp.billingStatus } : {}),
        is_office_expense:  exp.isOfficeExpense ?? false,
        foreign_vendor:     exp.foreignVendor   ?? false,
        ...(exp.voucherId     ? { voucher_id:     exp.voucherId }     : {}),
        ...(exp.voucherDate   ? { voucher_date:   exp.voucherDate }   : {}),
        ...(exp.voucherStatus ? { voucher_status: exp.voucherStatus } : {}),
      };
      // Validate project_id is a clean integer (not NaN, not float)
      if (row.project_id !== null && (!Number.isFinite(row.project_id) || row.project_id !== Math.floor(row.project_id))) {
        console.warn(`[EXPENSES bulkImport] Row ${idx}: invalid project_id "${exp.projectId}", setting to null`);
        row.project_id = null;
      }
      return row;
    });

    console.log('[EXPENSES bulkImport] Inserting', dbRows.length, 'rows. Sample:', JSON.stringify(dbRows[0]));

    // Attempt batch insert (all at once)
    const { data, error } = await supabase
      .from('expenses')
      .insert(dbRows)
      .select();

    if (!error) {
      // Use actual returned row count — RLS policies can silently block inserts
      results.successCount = Array.isArray(data) ? data.length : dbRows.length;
      if (results.successCount === 0) {
        results.errors.push('Insert returned no rows — check RLS policies or NOT NULL constraints');
      }
      if (results.successCount > 0) await fetchExpenses();
      return results;
    }

    // Batch failed — log full error, then fall back to one-by-one
    console.error('[EXPENSES bulkImport] Batch insert failed:', JSON.stringify(error));

    // One-by-one fallback for partial success
    for (let i = 0; i < dbRows.length; i++) {
      const { error: rowErr } = await supabase
        .from('expenses')
        .insert([dbRows[i]])
        .select();

      if (rowErr) {
        console.error(`[EXPENSES bulkImport] Row ${i} failed:`, JSON.stringify(rowErr), 'Payload:', JSON.stringify(dbRows[i]));
        results.errors.push(`Row ${i + 1}: ${rowErr.message || rowErr.code || 'Unknown DB error'} (code: ${rowErr.code || 'none'})`);
      } else {
        results.successCount++;
      }
    }

    if (results.successCount > 0) await fetchExpenses();
    return results;
  };

  // ── Link / unlink expense ↔ reimbursement ──
  const linkExpenseToReimbursement = async (expenseId, reimbursementId) => {
    try {
      await requireSession();
      const { error: err1 } = await supabase
        .from('expenses')
        .update({ linked_reimbursement_id: reimbursementId, receipt_status: 'linked' })
        .eq('id', expenseId);
      if (err1) throw err1;
      const { error: err2 } = await supabase
        .from('reimbursements')
        .update({ linked_expense_id: expenseId })
        .eq('id', reimbursementId);
      if (err2) throw err2;
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err) };
    }
  };

  const unlinkExpense = async (expenseId, reimbursementId) => {
    try {
      await requireSession();
      const { error: err1 } = await supabase
        .from('expenses')
        .update({ linked_reimbursement_id: null, receipt_status: 'missing' })
        .eq('id', expenseId);
      if (err1) throw err1;
      if (reimbursementId) {
        const { error: err2 } = await supabase
          .from('reimbursements')
          .update({ linked_expense_id: null })
          .eq('id', reimbursementId);
        if (err2) throw err2;
      }
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err) };
    }
  };

  const updateReceiptStatus = async (expenseId, status) => {
    try {
      await requireSession();
      const { error: err } = await supabase
        .from('expenses')
        .update({ receipt_status: status })
        .eq('id', expenseId);
      if (err) throw err;
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err) };
    }
  };

  // ── VOUCHER FUNCTIONS (mirrors useSupabaseReimbursements pattern) ──────
  const createExpenseVoucher = async (expenseIds, voucherMeta = {}) => {
    try {
      await requireSession();
      const voucherId = voucherMeta.voucherId || `VCH-${Date.now().toString(36).toUpperCase()}`;
      const { error } = await supabase
        .from('expenses')
        .update({
          voucher_id:     voucherId,
          voucher_date:   voucherMeta.date || new Date().toISOString().split('T')[0],
          voucher_status: 'draft',
          updated_at:     new Date().toISOString(),
        })
        .in('id', expenseIds);
      if (error) throw error;
      await fetchExpenses();
      return { success: true, voucherId };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create voucher for', entity: 'expenses' }) };
    }
  };

  const updateExpenseVoucherStatus = async (voucherId, newStatus) => {
    try {
      await requireSession();
      const updates = { voucher_status: newStatus, updated_at: new Date().toISOString() };
      const { error } = await supabase
        .from('expenses')
        .update(updates)
        .eq('voucher_id', voucherId);
      if (error) throw error;
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update voucher status for', entity: 'expenses' }) };
    }
  };

  const removeExpenseFromVoucher = async (expenseId) => {
    try {
      await requireSession();
      const { error } = await supabase
        .from('expenses')
        .update({ voucher_id: null, voucher_date: null, voucher_status: null, updated_at: new Date().toISOString() })
        .eq('id', expenseId);
      if (error) throw error;
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'remove from voucher for', entity: 'expense' }) };
    }
  };

  const deleteExpenseVoucher = async (voucherId) => {
    try {
      await requireSession();
      const { error } = await supabase
        .from('expenses')
        .update({ voucher_id: null, voucher_date: null, voucher_status: null, updated_at: new Date().toISOString() })
        .eq('voucher_id', voucherId);
      if (error) throw error;
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete voucher for', entity: 'expenses' }) };
    }
  };

  return { expenses, loading, createExpense, updateExpense, deleteExpense, bulkImportExpenses, linkExpenseToReimbursement, unlinkExpense, updateReceiptStatus, createExpenseVoucher, updateExpenseVoucherStatus, removeExpenseFromVoucher, deleteExpenseVoucher, refetch: fetchExpenses };
};

// ============================================================================
// 8. OFFICE OVERHEADS YEARLY HOOK (6 fields)
// ============================================================================
export const useSupabaseOfficeOverheads = ({ enabled = true } = {}) => {
  const [overheads, setOverheads] = useState([]);
  const [loading, setLoading] = useState(enabled);

  const fetchOverheads = async () => {
    try {
      const { data, error } = await supabase
        .from('office_overheads_yearly')
        .select('*')
        .order('year', { ascending: false })
        .order('category');
      
      if (error) throw error;
      
      const mapped = (data || []).map(o => ({
        id: o.id,
        year: o.year,
        category: o.category,
        subcategory: o.subcategory,
        amount: o.amount ?? 0,
        notes: o.notes ?? null,
        officeLocation: o.office_location || 'NL',
        createdAt: o.created_at,
        updatedAt: o.updated_at,
        // Currency fields (fallback for pre-migration rows)
        amountOriginal:   o.amount_original   ?? o.amount ?? 0,
        currencyOriginal: o.currency_original || 'INR',
        fxRateToInr:      o.fx_rate_to_inr    ?? 1,
        amountInInr:      o.amount_in_inr     ?? o.amount ?? 0,
        expenseDate:      o.expense_date      || null,
        importSource:     o.import_source     || null,
        importBatchId:    o.import_batch_id   || null
      }));
      
      setOverheads(mapped);
    } catch (err) {
      console.error('Error fetching office overheads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enabled) fetchOverheads(); }, [enabled]);

  const createOfficeOverhead = async (overheadData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const dbData = {
        year: overheadData.year,
        category: overheadData.category,
        subcategory: overheadData.subcategory,
        amount: overheadData.amount ?? 0,
        notes: overheadData.notes ?? null,
        office_location: overheadData.officeLocation || 'NL',
        // Currency fields
        amount_original:   overheadData.amountOriginal   ?? overheadData.amount ?? 0,
        currency_original: overheadData.currencyOriginal || 'INR',
        fx_rate_to_inr:    overheadData.fxRateToInr      ?? 1,
        amount_in_inr:     overheadData.amountInInr       ?? overheadData.amount ?? 0,
        expense_date:      overheadData.expenseDate       || null,
        import_source:     overheadData.importSource      || 'manual',
        import_batch_id:   overheadData.importBatchId     || null
      };

      const { data, error } = await supabase
        .from('office_overheads_yearly')
        .upsert([dbData], {
          onConflict: 'year,category,subcategory',
          ignoreDuplicates: false
        })
        .select()
        .maybeSingle();
      
      if (error) throw error;
      await fetchOverheads();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating office overhead:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'office overhead' }) };
    }
  };

  const updateOfficeOverhead = async (overheadId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const dbUpdates = {};
      if (updates.year !== undefined) dbUpdates.year = updates.year;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.subcategory !== undefined) dbUpdates.subcategory = updates.subcategory;
      if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
      if (updates.officeLocation !== undefined) dbUpdates.office_location = updates.officeLocation;
      // Currency fields
      if (updates.amountOriginal   !== undefined) dbUpdates.amount_original   = updates.amountOriginal;
      if (updates.currencyOriginal !== undefined) dbUpdates.currency_original = updates.currencyOriginal;
      if (updates.fxRateToInr      !== undefined) dbUpdates.fx_rate_to_inr    = updates.fxRateToInr;
      if (updates.amountInInr      !== undefined) dbUpdates.amount_in_inr     = updates.amountInInr;
      if (updates.expenseDate      !== undefined) dbUpdates.expense_date      = updates.expenseDate;

      const { data, error } = await supabase
        .from('office_overheads_yearly')
        .update(dbUpdates)
        .eq('id', overheadId)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      await fetchOverheads();
      return { success: true, data };
    } catch (err) {
      console.error('Error updating office overhead:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'office overhead' }) };
    }
  };

  const deleteOfficeOverhead = async (overheadId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const { error } = await supabase
        .from('office_overheads_yearly')
        .delete()
        .eq('id', overheadId);
      
      if (error) throw error;
      await fetchOverheads();
      return { success: true };
    } catch (err) {
      console.error('Error deleting office overhead:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'office overhead' }) };
    }
  };

  const bulkCreateOfficeOverheads = async (overheadRows, options = {}) => {
    try {
      await requireSession();

      const batchId = options.batchId || `import_${Date.now()}`;
      const dbRows = overheadRows.map(row => ({
        year: row.year,
        category: row.category,
        subcategory: row.subcategory,
        amount: row.amount ?? 0,
        notes: row.notes ?? null,
        office_location: row.officeLocation || 'NL',
        amount_original:   row.amountOriginal   ?? row.amount ?? 0,
        currency_original: row.currencyOriginal || 'INR',
        fx_rate_to_inr:    row.fxRateToInr      ?? 1,
        amount_in_inr:     row.amountInInr       ?? row.amount ?? 0,
        expense_date:      row.expenseDate       || null,
        import_source:     row.importSource      || 'csv-import',
        import_batch_id:   batchId
      }));

      // Bank imports: use plain insert (each txn is unique).
      // Manual/CSV imports: use upsert to merge by year+category+subcategory.
      const usePlainInsert = options.insertMode === 'insert';
      const query = usePlainInsert
        ? supabase.from('office_overheads_yearly').insert(dbRows).select()
        : supabase.from('office_overheads_yearly').upsert(dbRows, {
            onConflict: 'year,category,subcategory',
            ignoreDuplicates: false
          }).select();

      const { data, error } = await query;

      if (error) throw error;
      await fetchOverheads();
      return { success: true, data, batchId };
    } catch (err) {
      console.error('Error bulk creating office overheads:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'bulk create', entity: 'office overheads' }) };
    }
  };

  return {
    overheads,
    loading,
    createOfficeOverhead,
    updateOfficeOverhead,
    deleteOfficeOverhead,
    bulkCreateOfficeOverheads,
    refetch: fetchOverheads
  };
};

// ============================================================================
// 8b. OFFICE OVERHEADS MONTHLY HOOK (read-only for profitability engine)
// ============================================================================
export const useSupabaseOfficeOverheadsMonthly = ({ enabled = true } = {}) => {
  const [overheads, setOverheads] = useState([]);
  const [loading, setLoading] = useState(enabled);

  const fetchOverheads = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('office_overheads_monthly')
        .select('*')
        .order('month', { ascending: false });

      if (error) throw error;

      const mapped = (data || []).map(o => ({
        id: o.id,
        officeLocation: o.office_location || 'NL',
        month: o.month,
        category: o.category || 'Total',
        amountInr: o.amount_inr ?? 0,
        notes: o.notes ?? null,
        importSource: o.import_source || null,
        createdAt: o.created_at,
        updatedAt: o.updated_at,
      }));

      setOverheads(mapped);
    } catch (err) {
      console.error('Error fetching monthly office overheads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enabled) fetchOverheads(); }, [enabled]);

  return {
    overheads,
    loading,
    refetch: fetchOverheads,
  };
};

// ============================================================================
// 9. SALARY MATRIX HISTORY HOOK (5 fields)
// ============================================================================
export const useSupabaseSalaryMatrix = ({ enabled = true } = {}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(enabled);

  const fetchSalaryMatrixHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('salary_matrix_history')
        .select('*')
        .order('effective_date', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const mapped = (data || []).map(h => ({
        id: h.id,
        effectiveDate: h.effective_date,
        endDate: h.end_date || null,
        changedBy: h.changed_by,
        changeReason: h.change_reason,
        matrix: h.matrix,
        createdAt: h.created_at
      }));
      
      setHistory(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching salary matrix history:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enabled) fetchSalaryMatrixHistory(); }, [enabled]);

  const createSalaryMatrixRevision = async (revisionData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // NOTE: id is NOT sent - let the database generate it
      const dbData = {
        effective_date: revisionData.effectiveDate,
        end_date: revisionData.endDate || null,
        changed_by: revisionData.changedBy,
        change_reason: revisionData.changeReason || 'Matrix update',
        matrix: revisionData.matrix
      };

      let result = await supabase
        .from('salary_matrix_history')
        .insert([dbData])
        .select()
        .maybeSingle();

      // If end_date column doesn't exist yet (migration not applied), retry without it
      if (result.error && result.error.message && result.error.message.includes('end_date')) {
        const { end_date: _, ...dbDataWithout } = dbData;
        result = await supabase
          .from('salary_matrix_history')
          .insert([dbDataWithout])
          .select()
          .maybeSingle();
      }

      if (result.error) throw result.error;

      const updatedHistory = await fetchSalaryMatrixHistory();
      return { success: true, data: result.data, history: updatedHistory };
    } catch (err) {
      console.error('Error creating salary matrix revision:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'salary matrix revision' }) };
    }
  };

  const updateSalaryMatrixRevision = async (id, revisionData) => {
    try {
      await requireSession();

      const dbData = {
        effective_date: revisionData.effectiveDate,
        end_date: revisionData.endDate !== undefined ? (revisionData.endDate || null) : undefined,
        changed_by: revisionData.changedBy,
        change_reason: revisionData.changeReason || 'Matrix update',
        ...(revisionData.matrix ? { matrix: revisionData.matrix } : {}),
      };
      // Remove undefined keys so we don't accidentally null out end_date when not provided
      Object.keys(dbData).forEach(k => dbData[k] === undefined && delete dbData[k]);

      let result = await supabase
        .from('salary_matrix_history')
        .update(dbData)
        .eq('id', id);

      // If end_date column doesn't exist yet (migration not applied), retry without it
      if (result.error && result.error.message && result.error.message.includes('end_date')) {
        const { end_date: _, ...dbDataWithout } = dbData;
        result = await supabase
          .from('salary_matrix_history')
          .update(dbDataWithout)
          .eq('id', id);
      }

      if (result.error) throw result.error;

      const updatedHistory = await fetchSalaryMatrixHistory();
      return { success: true, history: updatedHistory };
    } catch (err) {
      console.error('Error updating salary matrix revision:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'salary matrix revision' }) };
    }
  };

  const deleteSalaryMatrixRevision = async (id) => {
    try {
      await requireSession();

      const { error } = await supabase
        .from('salary_matrix_history')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const updatedHistory = await fetchSalaryMatrixHistory();
      return { success: true, history: updatedHistory };
    } catch (err) {
      console.error('Error deleting salary matrix revision:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'salary matrix revision' }) };
    }
  };

  return {
    history,
    loading,
    fetchSalaryMatrixHistory,
    createSalaryMatrixRevision,
    updateSalaryMatrixRevision,
    deleteSalaryMatrixRevision,
    refetch: fetchSalaryMatrixHistory
  };
};

// ============================================================================
// 10a. NL SALARY MATRIX HOOK
// ============================================================================
// Mirrors useSupabaseSalaryMatrix but targets salary_matrix_history_nl.
// All amounts are in EUR. matrix JSONB keyed by schaal ("1"-"10", "c_1"-"c_4")
// then trede ("0"-"9"). custom_columns JSONB: [{id, name}].
// ============================================================================
export const useSupabaseSalaryMatrixNL = ({ enabled = true } = {}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(enabled);

  const fetchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('salary_matrix_history_nl')
        .select('*')
        .order('effective_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped = (data || []).map(h => ({
        id: h.id,
        effectiveDate: h.effective_date,
        endDate: h.end_date || null,
        changedBy: h.changed_by,
        changeReason: h.change_reason,
        matrix: h.matrix || {},
        customColumns: h.custom_columns || [],
        customTredes: h.custom_tredes || [],
        createdAt: h.created_at,
      }));

      setHistory(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching NL salary matrix history:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enabled) fetchHistory(); }, [enabled]);

  const createRevision = async (revisionData) => {
    try {
      await requireSession();

      const dbData = {
        effective_date:  revisionData.effectiveDate,
        end_date:        revisionData.endDate || null,
        changed_by:      revisionData.changedBy,
        change_reason:   revisionData.changeReason || 'Matrix update',
        matrix:          revisionData.matrix || {},
        custom_columns:  revisionData.customColumns || [],
        custom_tredes:   revisionData.customTredes || [],
      };

      const { data, error } = await supabase
        .from('salary_matrix_history_nl')
        .insert([dbData])
        .select()
        .maybeSingle();

      if (error) throw error;

      const updatedHistory = await fetchHistory();
      return { success: true, data, history: updatedHistory };
    } catch (err) {
      console.error('Error creating NL salary matrix revision:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'NL salary matrix revision' }) };
    }
  };

  const updateRevision = async (id, revisionData) => {
    try {
      await requireSession();

      const dbData = {
        effective_date:  revisionData.effectiveDate,
        end_date:        revisionData.endDate !== undefined ? (revisionData.endDate || null) : undefined,
        changed_by:      revisionData.changedBy,
        change_reason:   revisionData.changeReason || 'Matrix update',
        ...(revisionData.matrix        ? { matrix:         revisionData.matrix }        : {}),
        ...(revisionData.customColumns ? { custom_columns: revisionData.customColumns } : {}),
        ...(revisionData.customTredes  ? { custom_tredes:  revisionData.customTredes }  : {}),
      };
      Object.keys(dbData).forEach(k => dbData[k] === undefined && delete dbData[k]);

      const { error } = await supabase
        .from('salary_matrix_history_nl')
        .update(dbData)
        .eq('id', id);

      if (error) throw error;

      const updatedHistory = await fetchHistory();
      return { success: true, history: updatedHistory };
    } catch (err) {
      console.error('Error updating NL salary matrix revision:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'NL salary matrix revision' }) };
    }
  };

  const deleteRevision = async (id) => {
    try {
      await requireSession();

      const { error } = await supabase
        .from('salary_matrix_history_nl')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const updatedHistory = await fetchHistory();
      return { success: true, history: updatedHistory };
    } catch (err) {
      console.error('Error deleting NL salary matrix revision:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'NL salary matrix revision' }) };
    }
  };

  return {
    history,
    loading,
    createRevision,
    updateRevision,
    deleteRevision,
    refetch: fetchHistory,
  };
};

// ============================================================================
// 10. SALARY SETTINGS HOOK — DEPRECATED
// ============================================================================
// salary_settings table is deprecated. Markup now lives in app_settings
// under key 'manualSalaryMarkup' (year→numeric map). Read via useSettings().
// This stub is kept so existing imports don't crash; remove after full cleanup.
// ============================================================================
export const useSupabaseSalarySettings = () => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[useSupabaseSalarySettings] DEPRECATED — markup now in app_settings.manualSalaryMarkup. Remove this import.');
  }
  const noop = async () => ({ success: true });
  return {
    salarySettings: [],
    loading: false,
    getManualSalaryMarkup: () => null,
    getEssentialProfitPercentage: () => 20,
    getManualSalaryMarkupObject: () => ({}),
    upsertSalarySettings: noop,
    clearManualSalaryMarkup: noop,
    refetch: async () => [],
  };
};

// ============================================================================
// 11. CALENDAR EVENTS HOOK
// ============================================================================
// NOTE: calendar_events.id is TEXT (not auto-generated), client generates UUID
// ============================================================================
export const useSupabaseCalendarEvents = ({ enabled = true } = {}) => {
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(enabled);

  const fetchCalendarEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .is('deleted_at', null)
        .order('date', { ascending: true });
      
      if (error) throw error;
      
      const mapped = (data || []).map(e => ({
        id: e.id,
        title: e.title,
        category: e.category || 'events',
        date: e.date,
        startDate: e.start_date || e.date,
        endDate: e.end_date || e.date,
        startTime: e.start_time,
        endTime: e.end_time,
        isAllDay: e.is_all_day ?? false,
        description: e.description || '',
        color: e.color || 'bg-gray-100 text-gray-800 border-gray-300',
        createdBy: e.created_by,
        createdAt: e.created_at,
        updatedAt: e.updated_at,
        msCalendarEventId: e.ms_calendar_event_id,
        source: { table: 'calendar_events', id: e.id },
        isEditable: true
      }));
      
      setCalendarEvents(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enabled) fetchCalendarEvents(); }, [enabled]);

  const createCalendarEvent = async (eventData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Get session for created_by
      // ========================================
      const authUserId = await requireSession();
      
      // ========================================
      // RESOLVE auth UUID → users.id (BIGINT)
      // calendar_events.created_by is BIGINT, not UUID
      // ========================================
      const createdByUserId = await normalizeUserIdToBigint(authUserId, 'calendar-create');
      

      // calendar_events.id is TEXT - generate UUID if not provided
      const eventId = eventData.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
      
      const dbData = {
        id: eventId,                                        // TEXT id (client-generated)
        title: eventData.title,
        category: eventData.category || 'events',
        date: eventData.date,
        start_date: eventData.startDate || eventData.date,
        end_date: eventData.endDate || eventData.startDate || eventData.date,
        start_time: eventData.startTime || null,
        end_time: eventData.endTime || null,
        is_all_day: eventData.isAllDay ?? false,
        description: eventData.description || '',
        color: eventData.color || 'bg-gray-100 text-gray-800 border-gray-300',
        created_by: createdByUserId,                        // BIGINT (resolved from auth UUID)
        ms_calendar_event_id: eventData.msCalendarEventId || null
      };


      const { data, error } = await supabase
        .from('calendar_events')
        .insert([dbData])
        .select()
        .single();
      
      if (error) {
        console.error('[CALENDAR] Supabase error:', error);
        throw error;
      }
      await fetchCalendarEvents();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating calendar event:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'calendar event' }) };
    }
  };

  const updateCalendarEvent = async (eventId, updates) => {
    try {
      // ========================================
      // VALIDATION
      // ========================================
      if (eventId === undefined || eventId === null || eventId === '') {
        return { success: false, error: 'Cannot update calendar event: eventId is required' };
      }

      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      const authUserId = await requireSession();

      // ========================================
      // RESOLVE auth UUID → users.id (BIGINT) for updated_by
      // (only used if column exists)
      // ========================================
      let updaterUserId = null;
      try {
        updaterUserId = await normalizeUserIdToBigint(authUserId, 'calendar-update');
      } catch (resolveErr) {
        console.warn('[CALENDAR] Could not resolve user ID for updated_by:', resolveErr.message);
      }

      // Build base update payload (always include updated_at)
      const dbUpdates = { 
        updated_at: new Date().toISOString()
      };
      
      // Add updated_by if we resolved the user ID
      if (updaterUserId !== null) {
        dbUpdates.updated_by = updaterUserId;
      }

      // Map field updates
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.date !== undefined) dbUpdates.date = updates.date;
      if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
      if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate;
      if (updates.startTime !== undefined) dbUpdates.start_time = updates.startTime;
      if (updates.endTime !== undefined) dbUpdates.end_time = updates.endTime;
      if (updates.isAllDay !== undefined) dbUpdates.is_all_day = updates.isAllDay;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.msCalendarEventId !== undefined) dbUpdates.ms_calendar_event_id = updates.msCalendarEventId;


      // Attempt update
      let { data, error } = await supabase
        .from('calendar_events')
        .update(dbUpdates)
        .eq('id', eventId)
        .select()
        .single();
      
      // Handle "column does not exist" error (42703) - retry without updated_by
      if (error && error.code === '42703' && dbUpdates.updated_by !== undefined) {
        console.warn('[CALENDAR] Column updated_by does not exist, retrying without it');
        delete dbUpdates.updated_by;
        
        const retryResult = await supabase
          .from('calendar_events')
          .update(dbUpdates)
          .eq('id', eventId)
          .select()
          .single();
        
        data = retryResult.data;
        error = retryResult.error;
      }

      if (error) {
        console.error('[CALENDAR] Update error:', error);
        throw error;
      }

      await fetchCalendarEvents();
      return { success: true, data };
    } catch (err) {
      console.error('[CALENDAR] Error updating calendar event:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'calendar event' }) };
    }
  };

  const deleteCalendarEvent = async (eventId) => {
    try {
      // ========================================
      // VALIDATION
      // ========================================
      if (eventId === undefined || eventId === null || eventId === '') {
        return { success: false, error: 'Cannot delete calendar event: eventId is required' };
      }

      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      const authUserId = await requireSession();

      // ========================================
      // RESOLVE auth UUID → users.id (BIGINT) for deleted_by
      // (only used if column exists)
      // ========================================
      let deleterUserId = null;
      try {
        deleterUserId = await normalizeUserIdToBigint(authUserId, 'calendar-delete');
      } catch (resolveErr) {
        console.warn('[CALENDAR] Could not resolve user ID for deleted_by:', resolveErr.message);
      }


      // Build soft delete payload (always include deleted_at)
      const deletePayload = { 
        deleted_at: new Date().toISOString()
      };
      
      // Add deleted_by if we resolved the user ID
      if (deleterUserId !== null) {
        deletePayload.deleted_by = deleterUserId;
      }

      // Attempt soft delete
      let { error } = await supabase
        .from('calendar_events')
        .update(deletePayload)
        .eq('id', eventId);
      
      // Handle "column does not exist" error (42703) - retry without deleted_by
      if (error && error.code === '42703' && deletePayload.deleted_by !== undefined) {
        console.warn('[CALENDAR] Column deleted_by does not exist, retrying without it');
        delete deletePayload.deleted_by;
        
        const retryResult = await supabase
          .from('calendar_events')
          .update(deletePayload)
          .eq('id', eventId);
        
        error = retryResult.error;
      }

      if (error) {
        console.error('[CALENDAR] Delete error:', error);
        throw error;
      }

      await fetchCalendarEvents();
      return { success: true };
    } catch (err) {
      console.error('[CALENDAR] Error deleting calendar event:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'calendar event' }) };
    }
  };

  return { 
    calendarEvents, 
    loading, 
    fetchCalendarEvents,
    createCalendarEvent, 
    updateCalendarEvent, 
    deleteCalendarEvent, 
    refetch: fetchCalendarEvents 
  };
};

// ============================================================================
// 12. ALLOCATIONS HOOK - uses tt_allocations table
// ============================================================================
export const useSupabaseAllocations = () => {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchAllocations = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tt_allocations')
        .select('id, user_id, project_id, week_start, allocated_hours, notes')
        .order('week_start', { ascending: false });
      
      if (error) throw error;
      
      const mapped = (data || []).map(a => ({
        id: a.id,
        userId: a.user_id,
        projectId: a.project_id,
        weekStart: a.week_start,
        allocatedHours: a.allocated_hours ?? 0,
        notes: a.notes ?? ''
      }));
      
      setAllocations(mapped);
    } catch (err) {
      console.error('Error fetching allocations:', err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllocations(); }, [fetchAllocations]);

  const createAllocation = async (allocationData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Get session for auth_user_id
      // ========================================
      const authUserId = await requireSession();
      

      // Note: id is NOT sent - let the database generate it
      const dbData = {
        user_id: allocationData.userId,
        project_id: allocationData.projectId,
        week_start: allocationData.weekStart,
        allocated_hours: allocationData.allocatedHours ?? 0,
        notes: allocationData.notes ?? '',
        auth_user_id: authUserId
      };


      const { data, error } = await supabase
        .from('tt_allocations')
        .insert([dbData])
        .select()
        .maybeSingle();
      
      if (error) {
        console.error('[ALLOCATIONS] Supabase error:', error);
        throw error;
      }
      await fetchAllocations();
      return { success: true, data };
    } catch (err) {
      console.error('Error creating allocation:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'allocation' }) };
    }
  };

  const updateAllocation = async (allocationId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      if (allocationId === undefined || allocationId === null || allocationId === '') {
        throw new Error('updateAllocation called without a valid allocationId');
      }

      const dbUpdates = {};
      if (updates.userId !== undefined) dbUpdates.user_id = updates.userId;
      if (updates.projectId !== undefined) dbUpdates.project_id = updates.projectId;
      if (updates.weekStart !== undefined) dbUpdates.week_start = updates.weekStart;
      if (updates.allocatedHours !== undefined) dbUpdates.allocated_hours = updates.allocatedHours;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;

      if (Object.keys(dbUpdates).length === 0) {
        return { success: false, error: 'No updates provided' };
      }

      const { data, error } = await supabase
        .from('tt_allocations')
        .update(dbUpdates)
        .eq('id', allocationId)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      if (!data) {
        throw new Error(`Allocation not found (no rows updated) for id=${allocationId}`);
      }
      await fetchAllocations();
      return { success: true, data };
    } catch (err) {
      console.error('Error updating allocation:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'allocation' }) };
    }
  };

  const deleteAllocation = async (allocationId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      if (allocationId === undefined || allocationId === null || allocationId === '') {
        throw new Error('deleteAllocation called without a valid allocationId');
      }

      const { error } = await supabase
        .from('tt_allocations')
        .delete()
        .eq('id', allocationId);
      
      if (error) throw error;
      await fetchAllocations();
      return { success: true };
    } catch (err) {
      console.error('Error deleting allocation:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'allocation' }) };
    }
  };

  return { 
    allocations, 
    loading,
    initialLoading,
    fetchAllocations,
    createAllocation, 
    updateAllocation, 
    deleteAllocation, 
    refetch: fetchAllocations 
  };
};

// ============================================================================
// 13. TEAMS HOOK (7 fields)
// ============================================================================
export const useSupabaseTeams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const mapTeamRow = (t) => ({
    id: t.id,
    name: t.name,
    code: t.code || '',
    color: t.color || '#3b82f6',
    outlineColor: t.color || '#3b82f6',
    isActive: t.is_active !== false,
    supervisorIds: normalizeIdArray(t.supervisor_ids || []),
    memberIds: []
  });

  const fetchTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name, code, color, is_active, supervisor_ids')
        .order('name');

      if (error) {
        // Fallback: if supervisor_ids column doesn't exist, fetch without it
        if (error.message && error.message.includes('supervisor_ids')) {
          console.warn('[useSupabaseTeams] supervisor_ids column not found, fetching without it');
          const fallback = await supabase
            .from('teams')
            .select('id, name, code, color, is_active')
            .order('name');
          if (fallback.error) throw fallback.error;
          setTeams((fallback.data || []).map(t => ({ ...mapTeamRow(t), supervisorIds: [] })));
          return;
        }
        throw error;
      }

      setTeams((data || []).map(mapTeamRow));
    } catch (err) {
      console.error('[useSupabaseTeams] Error fetching teams:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeams(); }, []);

  const createTeam = async (teamData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      // Note: id is NOT sent - let the database generate it (BIGINT GENERATED)
      const dbData = {
        name: teamData.name,
        code: teamData.code || '',
        color: teamData.color || '#3b82f6',
        is_active: teamData.isActive !== false,
        supervisor_ids: normalizeIdArray(teamData.supervisorIds || [])
      };


      const { data, error } = await supabase
        .from('teams')
        .insert([dbData])
        .select('id, name, code, color, is_active, supervisor_ids')
        .single();
      
      if (error) {
        console.error('[useSupabaseTeams] createTeam ERROR:', error);
        return { success: false, error: error.message, code: error.code };
      }
      
      await fetchTeams();
      return { success: true, data };
    } catch (err) {
      console.error('[useSupabaseTeams] Error creating team:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'team' }) };
    }
  };

  const updateTeam = async (teamId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      if (teamId === undefined || teamId === null || teamId === '') {
        throw new Error('updateTeam called without a valid teamId');
      }

      const dbUpdates = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.code !== undefined) dbUpdates.code = updates.code;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
      if (updates.supervisorIds !== undefined) {
        dbUpdates.supervisor_ids = normalizeIdArray(updates.supervisorIds || []);
      }

      if (Object.keys(dbUpdates).length === 0) {
        return { success: false, error: 'No updates provided' };
      }


      const { data, error } = await supabase
        .from('teams')
        .update(dbUpdates)
        .eq('id', teamId)
        .select('id, name, code, color, is_active, supervisor_ids')
        .single();
      
      if (error) throw error;
      
      await fetchTeams();
      return { success: true, data };
    } catch (err) {
      console.error('[useSupabaseTeams] Error updating team:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'team' }) };
    }
  };

  const deleteTeam = async (teamId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);
      
      if (error) throw error;
      await fetchTeams();
      return { success: true };
    } catch (err) {
      console.error('[useSupabaseTeams] Error deleting team:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'team' }) };
    }
  };

  return { teams, loading, createTeam, updateTeam, deleteTeam, refetch: fetchTeams };
};

// ============================================================================
// 13b. TEAM PROJECTS HOOK - Manages team_projects join table
// ============================================================================
// Links projects to teams via public.team_projects (team_id, project_id, is_primary)
// Used for team-based project visibility for supervisors
// ============================================================================
export const useSupabaseTeamProjects = () => {
  const [teamProjects, setTeamProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTeamProjects = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('team_projects')
        .select('team_id, project_id, is_primary');
      
      if (error) throw error;
      
      const mapped = (data || []).map(tp => ({
        teamId: normalizeId(tp.team_id),
        projectId: normalizeId(tp.project_id),
        isPrimary: tp.is_primary ?? false
      }));
      
      setTeamProjects(mapped);
    } catch (err) {
      console.error('[useSupabaseTeamProjects] Error fetching:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeamProjects(); }, [fetchTeamProjects]);

  // Get project IDs linked to a specific team
  const getProjectIdsForTeam = useCallback((teamId) => {
    const normalizedTeamId = normalizeId(teamId);
    return teamProjects
      .filter(tp => tp.teamId === normalizedTeamId)
      .map(tp => tp.projectId);
  }, [teamProjects]);

  // Get team IDs linked to a specific project
  const getTeamIdsForProject = useCallback((projectId) => {
    const normalizedProjectId = normalizeId(projectId);
    return teamProjects
      .filter(tp => tp.projectId === normalizedProjectId)
      .map(tp => tp.teamId);
  }, [teamProjects]);

  // Set the projects linked to a team (replaces existing links for that team)
  const setProjectsForTeam = async (teamId, projectIds) => {
    try {
      await requireSession();
      
      const normalizedTeamId = normalizeId(teamId);
      const normalizedProjectIds = normalizeIdArray(projectIds);
      
      // Get current project IDs for this team
      const currentProjectIds = getProjectIdsForTeam(normalizedTeamId);
      
      // Calculate what to add and remove
      const toAdd = normalizedProjectIds.filter(pid => !currentProjectIds.includes(pid));
      const toRemove = currentProjectIds.filter(pid => !normalizedProjectIds.includes(pid));
      
      // Remove unlinked projects
      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('team_projects')
          .delete()
          .eq('team_id', normalizedTeamId)
          .in('project_id', toRemove);
        
        if (deleteError) {
          console.error('[TEAM_PROJECTS] Delete error:', deleteError);
          throw deleteError;
        }
      }
      
      // Add new project links
      if (toAdd.length > 0) {
        const newLinks = toAdd.map(projectId => ({
          team_id: normalizedTeamId,
          project_id: projectId,
          is_primary: false
        }));
        
        const { error: insertError } = await supabase
          .from('team_projects')
          .insert(newLinks);
        
        if (insertError) {
          console.error('[TEAM_PROJECTS] Insert error:', insertError);
          throw insertError;
        }
      }
      
      // Refetch to update local state
      await fetchTeamProjects();
      return { success: true };
    } catch (err) {
      console.error('[useSupabaseTeamProjects] Error setting projects:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'team projects' }) };
    }
  };

  return {
    teamProjects,
    loading,
    getProjectIdsForTeam,
    getTeamIdsForProject,
    setProjectsForTeam,
    refetch: fetchTeamProjects
  };
};

// ============================================================================
// 14. TIME OFF REQUESTS HOOK (13 fields)
// ============================================================================
export const useSupabaseTimeOffRequests = () => {
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTimeOffRequests = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('time_off_requests')
        .select('*')
        .order('requested_date', { ascending: false });
      
      if (error) throw error;
      
      const mapped = (data || []).map(t => ({
        id: t.id,
        userId: normalizeId(t.user_id),
        type: t.type,
        startDate: t.start_date,
        endDate: t.end_date,
        status: t.status || 'pending_supervisor',
        supervisorApproval: t.supervisor_approval || 'pending',
        adminApproval: t.admin_approval || 'pending',
        days: t.days,
        workingDays: t.working_days,
        requestedDate: t.requested_date,
        reason: t.reason || null,
        rejectionReason: t.rejection_reason || null,
        approvedBy: t.approved_by != null ? normalizeId(t.approved_by) : null,
        approvedAt: t.approved_at || null
      }));
      
      setTimeOffRequests(mapped);
    } catch (err) {
      console.error('[useSupabaseTimeOffRequests] Error fetching time off requests:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTimeOffRequests(); }, [fetchTimeOffRequests]);

  const createTimeOffRequest = async (requestData) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Get session for auth_user_id
      // ========================================
      const authUserId = await requireSession();
      

      // Note: id is auto-generated by DB
      const dbData = {
        user_id: normalizeId(requestData.userId),
        type: requestData.type,
        start_date: requestData.startDate,
        end_date: requestData.endDate,
        status: requestData.status || 'pending_supervisor',
        supervisor_approval: requestData.supervisorApproval || 'pending',
        admin_approval: requestData.adminApproval || 'pending',
        days: requestData.days ?? null,
        working_days: requestData.workingDays ?? null,
        requested_date: requestData.requestedDate || new Date().toISOString().split('T')[0],
        reason: requestData.reason || null,
        rejection_reason: requestData.rejectionReason || null,
        approved_by: requestData.approvedBy != null ? normalizeId(requestData.approvedBy) : null,
        approved_at: requestData.approvedAt || null,
        auth_user_id: authUserId
      };


      const { data, error } = await supabase
        .from('time_off_requests')
        .insert([dbData])
        .select()
        .single();
      
      if (error) {
        console.error('[TIME_OFF] Supabase error:', error);
        throw error;
      }
      
      await fetchTimeOffRequests();
      return { success: true, data };
    } catch (err) {
      console.error('[useSupabaseTimeOffRequests] Error creating time off request:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'time off request' }) };
    }
  };

  const updateTimeOffRequest = async (requestId, updates) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      if (requestId === undefined || requestId === null || requestId === '') {
        throw new Error('updateTimeOffRequest called without a valid requestId');
      }

      const idValue = (typeof requestId === 'string' && /^\d+$/.test(requestId)) ? Number(requestId) : requestId;

      const dbUpdates = {};
      if (updates.userId !== undefined) dbUpdates.user_id = normalizeId(updates.userId);
      if (updates.type !== undefined) dbUpdates.type = updates.type;
      if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
      if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.supervisorApproval !== undefined) dbUpdates.supervisor_approval = updates.supervisorApproval;
      if (updates.adminApproval !== undefined) dbUpdates.admin_approval = updates.adminApproval;
      if (updates.days !== undefined) dbUpdates.days = updates.days;
      if (updates.workingDays !== undefined) dbUpdates.working_days = updates.workingDays;
      if (updates.requestedDate !== undefined) dbUpdates.requested_date = updates.requestedDate;
      if (updates.reason !== undefined) dbUpdates.reason = updates.reason;
      if (updates.rejectionReason !== undefined) dbUpdates.rejection_reason = updates.rejectionReason;
      if (updates.approvedBy !== undefined) {
        dbUpdates.approved_by = updates.approvedBy == null ? null : normalizeId(updates.approvedBy);
      }
      if (updates.approvedAt !== undefined) dbUpdates.approved_at = updates.approvedAt;

      if (Object.keys(dbUpdates).length === 0) {
        return { success: false, error: 'No updates provided' };
      }

      const { data, error } = await supabase
        .from('time_off_requests')
        .update(dbUpdates)
        .eq('id', idValue)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      if (!data) {
        throw new Error(`Time off request not found (no rows updated) for id=${idValue}`);
      }
      await fetchTimeOffRequests();
      return { success: true, data };
    } catch (err) {
      console.error('[useSupabaseTimeOffRequests] Error updating time off request:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'time off request' }) };
    }
  };

  const deleteTimeOffRequest = async (requestId) => {
    try {
      // ========================================
      // RLS COMPLIANCE: Require session
      // ========================================
      await requireSession();

      if (requestId === undefined || requestId === null || requestId === '') {
        throw new Error('deleteTimeOffRequest called without a valid requestId');
      }

      const idValue = (typeof requestId === 'string' && /^\d+$/.test(requestId)) ? Number(requestId) : requestId;

      const { error } = await supabase
        .from('time_off_requests')
        .delete()
        .eq('id', idValue);
      
      if (error) throw error;
      await fetchTimeOffRequests();
      return { success: true };
    } catch (err) {
      console.error('[useSupabaseTimeOffRequests] Error deleting time off request:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'time off request' }) };
    }
  };

  return { 
    timeOffRequests, 
    loading, 
    createTimeOffRequest, 
    updateTimeOffRequest, 
    deleteTimeOffRequest, 
    refetch: fetchTimeOffRequests 
  };
};

// Alias for backward compatibility with App.js import
export const useSupabaseTimeOff = useSupabaseTimeOffRequests;

// ============================================================================
// INQUIRIES (Pipeline / Leads)
// ============================================================================
// Tables: inquiries, inquiry_followups
// Role restriction: admin / admin-pa only (enforced at UI level)
// ============================================================================

export const useSupabaseInquiries = ({ enabled = true } = {}) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(enabled);

  // ── Map DB → App ──────────────────────────────────────────────────
  const mapRow = (r) => ({
    id: r.id,
    clientId: r.client_id,
    clientOrgNameOverride: r.client_org_name_override ?? '',
    clientNameOverride: r.client_name_override ?? '',
    clientPhoneOverride: r.client_phone_override ?? '',
    clientEmailOverride: r.client_email_override ?? '',
    clientAddressOverride: r.client_address_override ?? '',
    clientLinkedinOverride: r.client_linkedin_override ?? '',
    clientInterestingLinksOverride: r.client_interesting_links_override ?? '',
    clientPersonalNotesOverride: r.client_personal_notes_override ?? '',
    projectName: r.project_name ?? '',
    inquiryDate: r.inquiry_date,
    platformType: r.platform_type ?? 'other',
    platformDetail: r.platform_detail ?? '',
    potential: r.potential ?? 'medium',
    // (#3) priority: always normalize to "low"|"medium"|"high"|"urgent" — never expose raw numeric
    priority: (() => {
      if (r.priority_level && ['low', 'medium', 'high', 'urgent'].includes(r.priority_level)) return r.priority_level;
      if (typeof r.priority === 'number') return r.priority <= 3 ? 'low' : r.priority >= 10 ? 'urgent' : r.priority >= 8 ? 'high' : 'medium';
      if (typeof r.priority === 'string' && ['low', 'medium', 'high', 'urgent'].includes(r.priority)) return r.priority;
      return 'medium';
    })(),
    projectType: r.project_type ?? '',
    locationCity: r.location_city ?? '',
    locationCoordinates: r.location_coordinates ?? '',
    siteDropboxLink: r.site_dropbox_link ?? '',
    siteNotes: r.site_notes ?? '',
    plotArea: r.plot_area ?? null,
    briefNotes: r.brief_notes ?? '',
    briefAreaStatementLink: r.brief_area_statement_link ?? '',
    estimatedFee: r.estimated_fee ?? null,
    estimatedStartDate: r.estimated_start_date ?? null,
    feeScenarios: r.fee_scenarios ?? [],
    historicalComparison: r.historical_comparison ?? {},
    feeOffers: r.fee_offers ?? [],
    nextSteps: r.next_steps ?? '',
    archived: r.archived ?? false,
    converted: r.converted ?? false,
    convertedProjectId: r.converted_project_id,
    // (#1) Four-state status model
    status: r.status ?? (r.converted ? 'converted' : r.archived ? 'archived' : 'active'),
    // (#5) Cached last follow-up date from trigger
    lastFollowupDate: r.last_followup_date ?? null,
    // Next follow-up date (user-set)
    nextFollowupDate: r.next_followup_date ?? null,
    // Pipeline calculator: "Meeting with the founders" touchpoint
    founderMeetingDate: r.founder_meeting_date ?? null,
    // (#8) Area statement versions (JSONB)
    areaStatements: r.area_statements ?? [],
    // Invoicing fields — used on contract PDFs and copied to client on conversion
    invoicingName: r.invoicing_name ?? '',
    invoicingAddress: r.invoicing_address ?? '',
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  });

  // ── Map App → DB ──────────────────────────────────────────────────
  const toDb = (d) => {
    const db = {};
    if (d.clientId !== undefined) db.client_id = d.clientId;
    if (d.clientOrgNameOverride !== undefined) db.client_org_name_override = d.clientOrgNameOverride || null;
    if (d.clientNameOverride !== undefined) db.client_name_override = d.clientNameOverride || null;
    if (d.clientPhoneOverride !== undefined) db.client_phone_override = d.clientPhoneOverride || null;
    if (d.clientEmailOverride !== undefined) db.client_email_override = d.clientEmailOverride || null;
    if (d.clientAddressOverride !== undefined) db.client_address_override = d.clientAddressOverride || null;
    if (d.clientLinkedinOverride !== undefined) db.client_linkedin_override = d.clientLinkedinOverride || null;
    if (d.clientInterestingLinksOverride !== undefined) db.client_interesting_links_override = d.clientInterestingLinksOverride || null;
    if (d.clientPersonalNotesOverride !== undefined) db.client_personal_notes_override = d.clientPersonalNotesOverride || null;
    if (d.invoicingName !== undefined) db.invoicing_name = d.invoicingName || null;
    if (d.invoicingAddress !== undefined) db.invoicing_address = d.invoicingAddress || null;
    if (d.projectName !== undefined) db.project_name = d.projectName || null;
    if (d.inquiryDate !== undefined) db.inquiry_date = d.inquiryDate;
    if (d.platformType !== undefined) db.platform_type = d.platformType;
    if (d.platformDetail !== undefined) db.platform_detail = d.platformDetail || null;
    if (d.potential !== undefined) db.potential = d.potential;
    // (#3) priority: write to both priority_level (new TEXT) and priority (legacy INT) for compat
    if (d.priority !== undefined) {
      const val = String(d.priority);
      if (['low', 'medium', 'high', 'urgent'].includes(val)) {
        db.priority_level = val;
        // Keep legacy numeric in sync: low=2, medium=5, high=9, urgent=10
        db.priority = val === 'low' ? 2 : val === 'urgent' ? 10 : val === 'high' ? 9 : 5;
      } else {
        // Numeric input (legacy path)
        const num = Math.max(0, Math.min(10, parseInt(d.priority) || 5));
        db.priority = num;
        db.priority_level = num <= 3 ? 'low' : num >= 10 ? 'urgent' : num >= 8 ? 'high' : 'medium';
      }
    }
    if (d.projectType !== undefined) db.project_type = d.projectType || null;
    if (d.locationCity !== undefined) db.location_city = d.locationCity || null;
    if (d.locationCoordinates !== undefined) db.location_coordinates = d.locationCoordinates || null;
    if (d.siteDropboxLink !== undefined) db.site_dropbox_link = d.siteDropboxLink || null;
    if (d.siteNotes !== undefined) db.site_notes = d.siteNotes || null;
    if (d.plotArea !== undefined) db.plot_area = d.plotArea || null;
    if (d.briefNotes !== undefined) db.brief_notes = d.briefNotes || null;
    if (d.briefAreaStatementLink !== undefined) db.brief_area_statement_link = d.briefAreaStatementLink || null;
    if (d.estimatedFee !== undefined) db.estimated_fee = d.estimatedFee || null;
    if (d.estimatedStartDate !== undefined) db.estimated_start_date = d.estimatedStartDate || null;
    if (d.feeScenarios !== undefined) db.fee_scenarios = d.feeScenarios;
    if (d.historicalComparison !== undefined) db.historical_comparison = d.historicalComparison;
    if (d.feeOffers !== undefined) db.fee_offers = d.feeOffers;
    if (d.nextSteps !== undefined) db.next_steps = d.nextSteps || null;
    if (d.convertedProjectId !== undefined) db.converted_project_id = d.convertedProjectId;
    // (#1) Four-state status — auto-sync legacy booleans so old code/reports stay consistent
    if (d.status !== undefined) {
      db.status = d.status;
      // Derived booleans — always kept in sync with status
      db.converted = (d.status === 'converted');
      db.archived = (d.status === 'archived');
    }
    // If caller explicitly sets booleans without status, still accept them (legacy path)
    if (d.status === undefined) {
      if (d.archived !== undefined) db.archived = !!d.archived;
      if (d.converted !== undefined) db.converted = !!d.converted;
    }
    // (#8) Area statement versions (JSONB)
    if (d.areaStatements !== undefined) db.area_statements = d.areaStatements;
    // Next follow-up date
    if (d.nextFollowupDate !== undefined) db.next_followup_date = d.nextFollowupDate || null;
    if (d.lastFollowupDate !== undefined) db.last_followup_date = d.lastFollowupDate || null;
    if (d.founderMeetingDate !== undefined) db.founder_meeting_date = d.founderMeetingDate || null;
    return db;
  };

  const fetchInquiries = async () => {
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .select('*')
        .order('inquiry_date', { ascending: false });
      if (error) throw error;
      setInquiries((data || []).map(mapRow));
    } catch (err) {
      console.error('[INQUIRIES] fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enabled) fetchInquiries(); }, [enabled]);

  const createInquiry = async (payload) => {
    try {
      await requireSession();
      // (#3) Resolve priority text
      const priVal = String(payload.priority || 'medium');
      const priLevel = ['low', 'medium', 'high'].includes(priVal) ? priVal : 'medium';
      const priNum = priLevel === 'low' ? 2 : priLevel === 'high' ? 9 : 5;

      // (A) Derive status + keep legacy booleans in sync from day 1
      const status = payload.status || 'active';

      const dbData = {
        client_id: payload.clientId ?? null,                // (B) ?? preserves 0
        client_org_name_override: payload.clientOrgNameOverride || null,
        inquiry_date: payload.inquiryDate || new Date().toISOString().slice(0, 10),
        platform_type: payload.platformType || 'other',     // (D) passes 'website' through
        platform_detail: payload.platformDetail || null,
        potential: payload.potential || 'medium',
        priority: priNum,                                    // Legacy numeric (kept for compat)
        priority_level: priLevel,                            // (#3) New text column
        project_type: payload.projectType || null,
        location_city: payload.locationCity || null,
        location_coordinates: payload.locationCoordinates || null,
        next_steps: payload.nextSteps || null,
        fee_scenarios: payload.feeScenarios || [],
        fee_offers: payload.feeOffers || [],
        status,                                              // (#1) Four-state status
        // (A) Legacy booleans — always consistent with status
        converted: status === 'converted',
        archived: status === 'archived',
        area_statements: payload.areaStatements || [],       // (#8) Area statement versions
        next_followup_date: payload.nextFollowupDate || null,
      };
      const { data, error } = await supabase
        .from('inquiries').insert([dbData]).select().single();
      if (error) throw error;
      await fetchInquiries();
      return { success: true, data: mapRow(data) };
    } catch (err) {
      console.error('[INQUIRIES] create error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'inquiry' }) };
    }
  };

  const updateInquiry = async (id, updates) => {
    try {
      await requireSession();
      const dbUpdates = toDb(updates);
      if (Object.keys(dbUpdates).length === 0) return { success: false, error: 'No updates' };
      const { data, error } = await supabase
        .from('inquiries').update(dbUpdates).eq('id', id).select().single();
      if (error) throw error;
      // Optimistic: patch local state
      setInquiries(prev => prev.map(i => i.id === id ? mapRow(data) : i));
      return { success: true, data: mapRow(data) };
    } catch (err) {
      console.error('[INQUIRIES] update error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'inquiry' }) };
    }
  };

  const deleteInquiry = async (id) => {
    try {
      await requireSession();
      const { error } = await supabase.from('inquiries').delete().eq('id', id);
      if (error) throw error;
      setInquiries(prev => prev.filter(i => i.id !== id));
      return { success: true };
    } catch (err) {
      console.error('[INQUIRIES] delete error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'inquiry' }) };
    }
  };

  // ── Follow-ups ────────────────────────────────────────────────────
  const fetchFollowups = async (inquiryId) => {
    try {
      const { data, error } = await supabase
        .from('inquiry_followups')
        .select('*')
        .eq('inquiry_id', inquiryId)
        .order('followup_date', { ascending: false });
      if (error) throw error;
      return { success: true, data: (data || []).map(f => ({
        id: f.id,
        inquiryId: f.inquiry_id,
        followupDate: f.followup_date,
        notes: f.notes ?? '',
        // (#4) Follow-up attribution (UUID for audit/RLS)
        followupByUserId: f.followup_by_user_id ?? null,
        // (#13) Employee BIGINT ID for UI dropdown matching
        followupByEmployeeId: f.followup_by_employee_id ?? null,
        followupByName: f.followup_by_name ?? '',
        createdAt: f.created_at,
      }))};
    } catch (err) {
      return { success: false, error: err.message, data: [] };
    }
  };

  const addFollowup = async (inquiryId, followup) => {
    try {
      const sessionUserId = await requireSession();
      const { data, error } = await supabase
        .from('inquiry_followups')
        .insert([{
          inquiry_id: inquiryId,
          followup_date: followup.followupDate,
          notes: followup.notes || '',
          // (C) Attribution: auth UUID for audit/RLS — defaults to session user
          followup_by_user_id: followup.followupByUserId ?? sessionUserId,
          // (#13) Employee BIGINT ID for UI dropdown matching
          followup_by_employee_id: followup.followupByEmployeeId != null
            ? parseInt(followup.followupByEmployeeId, 10) : null,
          followup_by_name: followup.followupByName ?? '',
        }])
        .select().single();
      if (error) throw error;
      return { success: true, data: {
        id: data.id,
        inquiryId: data.inquiry_id,
        followupDate: data.followup_date,
        notes: data.notes,
        followupByUserId: data.followup_by_user_id ?? null,
        followupByEmployeeId: data.followup_by_employee_id ?? null,
        followupByName: data.followup_by_name ?? '',
        createdAt: data.created_at,
      }};
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'follow-up' }) };
    }
  };

  const deleteFollowup = async (id) => {
    try {
      await requireSession();
      const { error } = await supabase.from('inquiry_followups').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'follow-up' }) };
    }
  };

  return {
    inquiries, loading,
    createInquiry, updateInquiry, deleteInquiry,
    fetchFollowups, addFollowup, deleteFollowup,
    refetch: fetchInquiries,
  };
};

// ============================================================================
// ADMIN FUNCTIONS - Edge Function Wrappers (using manual fetch)
// ============================================================================
// These functions call secure Edge Functions that use the service role key
// to perform admin-only operations (create users, reset passwords).
// The Edge Functions verify the caller is an admin before proceeding.
//
// NOTE: We use manual fetch() instead of supabase.functions.invoke() because
// invoke() was not reliably passing the Authorization header, causing 401 errors.
// ============================================================================

/**
 * Helper: Call a Supabase Edge Function with explicit Authorization + apikey headers.
 * Uses manual fetch() for deterministic header passing.
 * Uses the SAME SUPABASE_URL and SUPABASE_ANON_KEY as the Supabase client to avoid project mismatch.
 * 
 * @param {string} functionName - Edge Function name (e.g., 'admin-create-user')
 * @param {Object} body - Request body to send as JSON
 * @returns {Promise<Object>} - Parsed JSON response data
 * @throws {Error} - If no session, or if the Edge Function returns non-2xx
 *                   Error has additional fields: status, code, edgeMessage, functionName
 */
const callEdgeFunction = async (functionName, body) => {
  // Get current session
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError) {
    const err = new Error('Session error: ' + sessionError.message);
    err.status = 0;
    err.edgeMessage = 'SESSION_ERROR';
    err.functionName = functionName;
    throw err;
  }
  
  if (!session?.access_token) {
    const err = new Error('No active session access token');
    err.status = 0;
    err.edgeMessage = 'NO_SESSION';
    err.functionName = functionName;
    throw err;
  }
  
  // Build URL and headers using IMPORTED constants (same as Supabase client)
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    const err = new Error('Missing SUPABASE_URL or SUPABASE_ANON_KEY from supabaseClient');
    err.status = 0;
    err.edgeMessage = 'CONFIG_ERROR';
    err.functionName = functionName;
    throw err;
  }
  
  const url = `${SUPABASE_URL}/functions/v1/${functionName}`;
  
  // Make the fetch request with explicit headers
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  
  // Read response
  const text = await res.text();
  
  // Try to parse as JSON
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    // If not valid JSON, keep as text for error message
    data = { rawText: text };
  }
  
  // Check for HTTP errors - throw structured error
  if (!res.ok) {
    const errorMsg = data?.error || data?.message || text || `HTTP ${res.status}`;
    const err = new Error(`Edge ${functionName} failed ${res.status}: ${errorMsg}`);
    err.status = res.status;
    err.code = data?.code;
    err.edgeMessage = data?.error || data?.message || errorMsg;
    err.functionName = functionName;
    throw err;
  }
  
  return data;
};

/**
 * Admin: Create or invite a user via Edge Function
 * - Creates Auth user with temporary password
 * - Links to existing public.users row OR creates new row
 * - Sets status = 'invited' for lifecycle management
 * 
 * @param {Object} params
 * @param {string} params.name - User's display name
 * @param {string} params.email - User's email address
 * @param {string} params.role - Role: 'admin', 'supervisor', or 'user'
 * @param {number|string|null} params.team_id - Team ID (bigint)
 * @param {string} params.tempPassword - Temporary password (min 6 chars)
 * @param {string} [params.job_role] - Optional job role
 * @param {number} [params.job_level] - Optional job level
 * @returns {Promise<{success: boolean, data?: Object, error?: string, errorDetails?: Object}>}
 */
export const adminCreateUser = async ({ name, email, role, team_id, tempPassword, ...optional }) => {
  try {
    const data = await callEdgeFunction('admin-create-user', {
      name,
      email,
      role,
      team_id,
      tempPassword,
      ...optional,
    });

    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to create user');
    }

    return { success: true, data };
  } catch (err) {
    // Preserve structured error fields for UI classification
    return { 
      success: false, 
      error: err.message || 'Failed to create user',
      errorDetails: {
        status: err.status,
        code: err.code,
        edgeMessage: err.edgeMessage,
        functionName: err.functionName,
      }
    };
  }
};

/**
 * Admin: Send password reset/setup email via Edge Function
 * - Sends a password reset email to the user
 * - User clicks link to set their own password
 * 
 * @param {Object} params
 * @param {string} params.email - User's email address
 * @param {string} [params.mode] - 'recovery' (default) or 'invite' (for users who haven't confirmed yet)
 * @returns {Promise<{success: boolean, data?: Object, error?: string, errorDetails?: Object}>}
 */
export const adminResetPassword = async ({ email, mode }) => {
  try {
    const data = await callEdgeFunction('admin-reset-password', {
      email,
      ...(mode && { mode }),
    });

    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to send password reset email');
    }

    return { success: true, data };
  } catch (err) {
    // Preserve structured error fields for UI classification
    return { 
      success: false, 
      error: err.message || 'Failed to send password reset email',
      errorDetails: {
        status: err.status,
        code: err.code,
        edgeMessage: err.edgeMessage,
        functionName: err.functionName,
      }
    };
  }
};

/**
 * Admin: Update a user's login email in auth.users via Edge Function
 * - Changes the email the user uses to sign in (auth.users table)
 * - Does NOT change the profile email in public.users
 * - Requires the user to have a linked auth account (auth_user_id)
 * 
 * @param {Object} params
 * @param {string} params.userId - The auth UUID (auth_user_id from public.users)
 * @param {string} params.newEmail - The new login email
 * @returns {Promise<{success: boolean, data?: Object, error?: string, errorDetails?: Object}>}
 */
export const adminUpdateEmail = async ({ userId, newEmail }) => {
  try {
    const data = await callEdgeFunction('admin-update-email', {
      userId,
      newEmail,
    });

    if (!data?.ok && !data?.user) {
      throw new Error(data?.error || 'Failed to update login email');
    }

    return { success: true, data };
  } catch (err) {
    // Preserve structured error fields for UI classification
    return { 
      success: false, 
      error: err.message || 'Failed to update login email',
      errorDetails: {
        status: err.status,
        code: err.code,
        edgeMessage: err.edgeMessage,
        functionName: err.functionName,
      }
    };
  }
};

// ============================================================================
// MS CALENDAR SETTINGS — Per-user calendar ID persistence
// ============================================================================
// Stores/retrieves ms_calendar_id per user so we don't re-create calendars.
// Uses user_profiles table (existing) with a JSONB or text column.
// If ms_calendar_settings column doesn't exist yet, falls back gracefully.
// ============================================================================

/**
 * Get stored MS Calendar settings for a user.
 * Returns { msCalendarId, msCalendarName } or null if not set.
 *
 * @param {number|string} userId - App user ID
 * @returns {Promise<{msCalendarSettings: object|null}>}
 */
export const getMsCalendarSettingsForUser = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('ms_calendar_settings')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      // Column may not exist yet — degrade gracefully
      if (error.code === '42703') {
        console.warn('[MS Calendar] ms_calendar_settings column not found in user_profiles — returning null');
        return { msCalendarSettings: null };
      }
      console.error('[MS Calendar] Error fetching calendar settings:', error);
      return { msCalendarSettings: null };
    }

    return { msCalendarSettings: data?.ms_calendar_settings || null };
  } catch (err) {
    console.error('[MS Calendar] Exception fetching calendar settings:', err);
    return { msCalendarSettings: null };
  }
};

/**
 * Store MS Calendar settings for a user.
 * Upserts into user_profiles with the calendar mapping data.
 *
 * @param {number|string} userId - App user ID
 * @param {object} settings - Calendar settings to store
 * @param {object} settings.calendarMappings - Map of eventType → { calendarId, calendarName }
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const setMsCalendarSettingsForUser = async (userId, settings) => {
  try {
    await requireSession();

    const { error } = await supabase
      .from('user_profiles')
      .upsert({
        user_id: userId,
        ms_calendar_settings: settings,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      // Column may not exist yet
      if (error.code === '42703') {
        console.warn('[MS Calendar] ms_calendar_settings column not found — settings not persisted');
        return { success: false, error: 'Column not available' };
      }
      console.error('[MS Calendar] Error saving calendar settings:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('[MS Calendar] Exception saving calendar settings:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get a specific MS Calendar ID for a given event type from stored settings.
 * Convenience wrapper around getMsCalendarSettingsForUser.
 *
 * @param {number|string} userId - App user ID
 * @param {string} eventType - Event type key (e.g. 'holidays', 'leaves')
 * @returns {Promise<string|null>} Calendar ID or null
 */
export const getMsCalendarIdForUser = async (userId, eventType) => {
  const { msCalendarSettings } = await getMsCalendarSettingsForUser(userId);
  if (!msCalendarSettings?.calendarMappings) return null;
  return msCalendarSettings.calendarMappings[eventType]?.calendarId || null;
};

/**
 * Store a specific MS Calendar ID for a given event type.
 * Merges with existing settings (does not overwrite other event types).
 *
 * @param {number|string} userId - App user ID
 * @param {string} eventType - Event type key
 * @param {string} calendarId - Microsoft Graph calendar ID
 * @param {string} [calendarName] - Calendar display name
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const setMsCalendarIdForUser = async (userId, eventType, calendarId, calendarName = '') => {
  // Read existing settings first to merge
  const { msCalendarSettings } = await getMsCalendarSettingsForUser(userId);
  const existing = msCalendarSettings || {};
  const mappings = existing.calendarMappings || {};

  mappings[eventType] = { calendarId, calendarName };

  return setMsCalendarSettingsForUser(userId, {
    ...existing,
    calendarMappings: mappings,
    lastUpdated: new Date().toISOString()
  });
};

// ============================================================================
// MS CALENDAR INTEGRATION STATUS — Admin-only master calendar
// ============================================================================
// Fetches the integration status from ms_calendar_integration_public view.
// This view exposes ONLY safe fields (no tokens) and is readable by all
// authenticated users so they can see whether the calendar is connected.
//
// If the view doesn't exist yet, falls back to ms_calendar_integration table
// with a safe column selection (no token fields).
// ============================================================================

/**
 * Fetch the admin-managed MS Calendar integration status.
 * Safe for all users — never exposes tokens or secrets.
 *
 * @returns {Promise<{
 *   isConnected: boolean,
 *   calendarName: string|null,
 *   lastSyncAt: string|null,
 *   connectedAt: string|null,
 *   error: string|null
 * }>}
 */
export const getMsCalendarIntegrationStatus = async () => {
  const fallback = {
    isConnected: false,
    calendarName: null,
    lastSyncAt: null,
    connectedAt: null,
    error: null
  };

  try {
    // Prefer the public view (no token columns)
    const { data, error } = await supabase
      .from('ms_calendar_integration_public')
      .select('is_connected, calendar_name, last_sync_at, connected_at')
      .limit(1)
      .maybeSingle();

    if (error) {
      // View may not exist yet — try the base table with safe columns
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn('[MS Calendar] ms_calendar_integration_public view not found — trying base table');
        return await getMsCalendarIntegrationStatusFallback();
      }
      // Permission error is expected for some roles if RLS blocks it
      if (error.code === '42501') {
        console.warn('[MS Calendar] No permission to read integration status');
        return fallback;
      }
      console.error('[MS Calendar] Error fetching integration status:', error);
      return { ...fallback, error: error.message };
    }

    if (!data) return fallback;

    return {
      isConnected: data.is_connected === true,
      calendarName: data.calendar_name || null,
      lastSyncAt: data.last_sync_at || null,
      connectedAt: data.connected_at || null,
      error: null
    };
  } catch (err) {
    console.error('[MS Calendar] Exception fetching integration status:', err);
    return { ...fallback, error: err.message };
  }
};

/**
 * Fallback: read from ms_calendar_integration base table with safe column selection.
 * Only used if the public view doesn't exist yet.
 * @private
 */
const getMsCalendarIntegrationStatusFallback = async () => {
  const fallback = {
    isConnected: false,
    calendarName: null,
    lastSyncAt: null,
    connectedAt: null,
    error: null
  };

  try {
    // Select ONLY safe columns — never tokens
    const { data, error } = await supabase
      .from('ms_calendar_integration')
      .select('is_connected, calendar_name, last_sync_at, connected_at')
      .limit(1)
      .maybeSingle();

    if (error) {
      // Table doesn't exist yet — not an error, integration just isn't set up
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        return fallback;
      }
      console.error('[MS Calendar] Fallback fetch error:', error);
      return { ...fallback, error: error.message };
    }

    if (!data) return fallback;

    return {
      isConnected: data.is_connected === true,
      calendarName: data.calendar_name || null,
      lastSyncAt: data.last_sync_at || null,
      connectedAt: data.connected_at || null,
      error: null
    };
  } catch (err) {
    return { ...fallback, error: err.message };
  }
};

/**
 * Check whether the current user's stored MS refresh token has been marked
 * invalid by the server-side renewal job (ms-subscription-renew sets
 * requires_reauth = true on invalid_grant). When true, the UI should prompt
 * the user to reconnect their calendar.
 *
 * Reads the user's own ms_token_store row (allowed by the "Users can manage
 * own tokens" RLS policy). Returns false on any error or missing row.
 *
 * @param {number} userId - The current user's id
 * @returns {Promise<boolean>} true if re-authentication is required
 */
export const getMsReauthRequired = async (userId) => {
  if (!userId) return false;
  try {
    const { data, error } = await supabase
      .from('ms_token_store')
      .select('requires_reauth')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      // No row, no permission, or table missing — treat as "no reauth needed"
      console.warn('[MS Calendar] Could not read reauth status:', error.message);
      return false;
    }
    return data?.requires_reauth === true;
  } catch (err) {
    console.warn('[MS Calendar] Exception reading reauth status:', err);
    return false;
  }
};

/**
 * Update the MS Calendar integration status (admin only).
 * Upserts the single integration record.
 *
 * @param {object} status - Fields to update
 * @param {boolean} [status.isConnected] - Whether calendar is connected
 * @param {string} [status.calendarName] - Display name of master calendar
 * @param {string} [status.lastSyncAt] - ISO timestamp of last sync
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateMsCalendarIntegrationStatus = async (status) => {
  try {
    await requireSession();

    const dbData = {};
    if (status.isConnected !== undefined) dbData.is_connected = status.isConnected;
    if (status.calendarName !== undefined) dbData.calendar_name = status.calendarName;
    if (status.lastSyncAt !== undefined) dbData.last_sync_at = status.lastSyncAt;
    if (status.connectedAt !== undefined) dbData.connected_at = status.connectedAt;
    dbData.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from('ms_calendar_integration')
      .upsert({
        id: 1,  // Single-row table — always ID 1
        ...dbData
      }, {
        onConflict: 'id'
      });

    if (error) {
      console.error('[MS Calendar] Error updating integration status:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('[MS Calendar] Exception updating integration status:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// VIEW FETCHERS - For Analytics & Reports (RLS-gated via views)
// ============================================================================
// These functions ONLY read from dedicated Supabase views.
// Views can have their own RLS policies to block specific roles (e.g., Admin-PA)
// without breaking operational pages that read base tables.
// ============================================================================

/**
 * Check if error indicates the queried relation (table/view) does not exist.
 * PostgREST returns code PGRST205 with HTTP 404 for missing relations.
 * We treat this as a graceful no-op since views may not be migrated yet.
 */
const isRelationNotFound = (error) => {
  if (!error) return false;
  return error.code === 'PGRST205';
};

/**
 * Fetch analytics dashboard data from the v_analytics_dashboard view.
 * This view contains pre-aggregated data for the analytics dashboard.
 * 
 * @param {Object} params - Optional filter parameters
 * @param {number} params.year - Filter by year
 * @param {number} params.month - Filter by month (1-12)
 * @param {number} params.teamId - Filter by team ID
 * @param {number} params.projectId - Filter by project ID
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchAnalyticsDashboardView = async (params = {}) => {
  try {
    
    let query = supabase
      .from('v_analytics_dashboard')
      .select('*');
    
    // Apply optional filters if provided
    if (params.year) {
      query = query.eq('year', params.year);
    }
    if (params.month) {
      query = query.eq('month', params.month);
    }
    if (params.teamId) {
      query = query.eq('team_id', params.teamId);
    }
    if (params.projectId) {
      query = query.eq('project_id', params.projectId);
    }
    
    const { data, error } = await query;

    if (error && isRelationNotFound(error)) {
      console.debug('[VIEWS][ANALYTICS] View does not exist yet, returning empty data');
      return { data: [], error: null };
    }
    if (error) {
      console.error('[VIEWS][ANALYTICS] Error:', error);
      return { data: null, error };
    }

    return { data: data || [], error: null };
  } catch (err) {
    console.error('[VIEWS][ANALYTICS] Exception:', err);
    return { data: null, error: err };
  }
};

/**
 * Fetch profitability report data from the v_report_profitability view.
 * This view contains pre-calculated project profitability metrics.
 * 
 * @param {Object} params - Optional filter parameters
 * @param {number} params.year - Filter by year
 * @param {number} params.month - Filter by month (1-12)
 * @param {number} params.projectId - Filter by project ID
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchProfitabilityReportView = async (params = {}) => {
  try {
    
    let query = supabase
      .from('v_report_profitability')
      .select('*');
    
    // Apply optional filters if provided
    if (params.year) {
      query = query.eq('year', params.year);
    }
    if (params.month) {
      query = query.eq('month', params.month);
    }
    if (params.projectId) {
      query = query.eq('project_id', params.projectId);
    }
    
    const { data, error } = await query;

    if (error && isRelationNotFound(error)) {
      console.debug('[VIEWS][PROFIT] View does not exist yet, returning empty data');
      return { data: [], error: null };
    }
    if (error) {
      console.error('[VIEWS][PROFIT] Error:', error);
      return { data: null, error };
    }

    return { data: data || [], error: null };
  } catch (err) {
    console.error('[VIEWS][PROFIT] Exception:', err);
    return { data: null, error: err };
  }
};

/**
 * Fetch salary cost report data from the v_report_salary_cost view.
 * This view contains pre-calculated salary cost analysis by user and period.
 * 
 * @param {Object} params - Optional filter parameters
 * @param {number} params.year - Filter by year
 * @param {number} params.month - Filter by month (1-12)
 * @param {number} params.userId - Filter by user ID
 * @param {number} params.teamId - Filter by team ID
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchSalaryCostReportView = async (params = {}) => {
  try {
    
    let query = supabase
      .from('v_report_salary_cost')
      .select('*');
    
    // Apply optional filters if provided
    if (params.year) {
      query = query.eq('year', params.year);
    }
    if (params.month) {
      query = query.eq('month', params.month);
    }
    if (params.userId) {
      query = query.eq('user_id', params.userId);
    }
    if (params.teamId) {
      query = query.eq('team_id', params.teamId);
    }
    
    const { data, error } = await query;

    if (error && isRelationNotFound(error)) {
      console.debug('[VIEWS][SALARY] View does not exist yet, returning empty data');
      return { data: [], error: null };
    }
    if (error) {
      console.error('[VIEWS][SALARY] Error:', error);
      return { data: null, error };
    }

    return { data: data || [], error: null };
  } catch (err) {
    console.error('[VIEWS][SALARY] Exception:', err);
    return { data: null, error: err };
  }
};

/**
 * Helper to check if an error indicates RLS permission denial.
 * Use this to show "no access" messages gracefully.
 * 
 * @param {Error|Object} error - The error from a Supabase query
 * @returns {boolean} True if the error is a permission denial (403/42501)
 */
export const isViewPermissionDenied = (error) => {
  if (!error) return false;
  
  // Check for HTTP 403
  if (error.status === 403) return true;
  
  // Check for PostgreSQL permission denied code
  if (error.code === '42501') return true;
  
  // Check error message for permission-related keywords
  const msg = (error.message || '').toLowerCase();
  if (msg.includes('permission denied') || msg.includes('rls') || msg.includes('policy')) {
    return true;
  }
  
  return false;
};

// Bundled export for view fetchers (for convenient import in App.js)
export const supabaseViews = {
  fetchAnalyticsDashboardView,
  fetchProfitabilityReportView,
  fetchSalaryCostReportView,
  isViewPermissionDenied
};

// ============================================================================
// 17. FEE INQUIRY MODELS HOOK (stateless — no auto-fetch on mount)
// ============================================================================
// Tables: fee_inquiry_models           (uuid pk, inquiry_id uuid FK)
//         fee_inquiry_model_comparables (model_id uuid FK, project_id bigint FK)
//         fee_inquiry_model_factors     (model_id uuid FK, factor_key text)
// RPC:    public.compute_fee_model(p_model_id uuid) → jsonb
//         public.seed_fee_model_defaults(p_model_id uuid) → void
// ============================================================================
export const useSupabaseFeeModels = () => {

  // 1) Create a new fee model version via RPC (deactivates previous, seeds factors)
  const createFeeModel = async ({
    inquiryId, name, currency, unit,
    inquiryProjectType, inquiryLocationCountry, inquiryLocationCity,
    inquiryScaleArea, inquiryScope,
  }) => {
    try {
      await requireSession();
      const { data, error } = await supabase.rpc('create_fee_model_version', {
        p_inquiry_id:               inquiryId,
        p_name:                     name || 'Model 1',
        p_currency:                 currency || 'INR',
        p_unit:                     unit || 'sqm',
        p_inquiry_project_type:     inquiryProjectType || null,
        p_inquiry_location_country: inquiryLocationCountry || null,
        p_inquiry_location_city:    inquiryLocationCity || null,
        p_inquiry_scale_area:       inquiryScaleArea != null
                                      ? parseFloat(inquiryScaleArea) : null,
        p_inquiry_scope:            inquiryScope || null,
      });
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('[FeeModels] createFeeModel error:', err);
      return { success: false, error: err.message };
    }
  };

  // 2) Fetch all fee models for an inquiry
  const fetchFeeModelsForInquiry = async (inquiryId) => {
    try {
      const { data, error } = await supabase
        .from('fee_inquiry_models')
        .select('*')
        .eq('inquiry_id', inquiryId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      console.error('[FeeModels] fetchFeeModelsForInquiry error:', err);
      return { success: false, error: err.message, data: [] };
    }
  };

  // 3) Upsert a comparable (project_id is BIGINT — parseInt for safety).
  // purpose:  { fee: bool, ctc: bool } controls which metrics use the row.
  const upsertComparable = async ({
    modelId, projectId, selectionSource, include, reason, snapshot, purpose,
  }) => {
    try {
      await requireSession();
      const pid = typeof projectId === 'string'
        ? parseInt(projectId, 10) : projectId;

      const row = {
        model_id:         modelId,
        project_id:       pid,
        selection_source: selectionSource || 'auto',
        include:          include !== false,
        reason:           reason || null,
        snapshot:         snapshot || null,
      };
      if (purpose) row.purpose = purpose;

      const { data, error } = await supabase
        .from('fee_inquiry_model_comparables')
        .upsert(row, { onConflict: 'model_id,project_id' })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('[FeeModels] upsertComparable error:', err);
      return { success: false, error: err.message };
    }
  };

  // 3b) Patch just the purpose of an existing comparable (avoids round-tripping
  //     the whole row — keeps the trigger that recomputes similarity quiet).
  const setComparablePurpose = async ({ modelId, projectId, purpose }) => {
    try {
      await requireSession();
      const pid = typeof projectId === 'string'
        ? parseInt(projectId, 10) : projectId;
      const { data, error } = await supabase
        .from('fee_inquiry_model_comparables')
        .update({ purpose: purpose || { fee: true, ctc: true } })
        .eq('model_id', modelId)
        .eq('project_id', pid)
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('[FeeModels] setComparablePurpose error:', err);
      return { success: false, error: err.message };
    }
  };

  // 4) Fetch comparables for a model (with joined project fields)
  const fetchComparables = async (modelId) => {
    try {
      const { data, error } = await supabase
        .from('fee_inquiry_model_comparables')
        .select(`
          id, model_id, project_id, selection_source,
          include, reason, snapshot,
          similarity_score, similarity_breakdown,
          projects:project_id ( id, name, budget, project_size, category, status )
        `)
        .eq('model_id', modelId)
        .order('include', { ascending: false });
      if (error) throw error;

      const mapped = (data || []).map(c => ({
        ...c,
        projectName:          c.projects?.name          || `Project #${c.project_id}`,
        projectBudget:        c.projects?.budget         || 0,
        projectSize:          c.projects?.project_size    || 0,
        projectCategory:      c.projects?.category        || '',
        projectStatus:        c.projects?.status          || '',
        purpose:              c.purpose || { fee: true, ctc: true },
        similarity_score:     c.similarity_score ?? null,
        similarity_breakdown: c.similarity_breakdown ?? null,
      }));
      return { success: true, data: mapped };
    } catch (err) {
      console.error('[FeeModels] fetchComparables error:', err);
      return { success: false, error: err.message, data: [] };
    }
  };

  // 5) Upsert a factor
  const upsertFactor = async ({ modelId, factorKey, value, weight }) => {
    try {
      await requireSession();
      const { data, error } = await supabase
        .from('fee_inquiry_model_factors')
        .upsert({
          model_id:   modelId,
          factor_key: factorKey,
          value:      parseFloat(value) || 0,
          weight:     weight !== undefined ? parseFloat(weight) : 1.0,
        }, { onConflict: 'model_id,factor_key' })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('[FeeModels] upsertFactor error:', err);
      return { success: false, error: err.message };
    }
  };

  // 5b) Fetch factors for a model
  const fetchFactors = async (modelId) => {
    try {
      const { data, error } = await supabase
        .from('fee_inquiry_model_factors')
        .select('*')
        .eq('model_id', modelId);
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      console.error('[FeeModels] fetchFactors error:', err);
      return { success: false, error: err.message, data: [] };
    }
  };

  // 6) Compute fee model via RPC, then compute metrics (cost-to-company + conversion)
  const computeFeeModel = async (modelId) => {
    try {
      const { data, error } = await supabase
        .rpc('compute_fee_model', { p_model_id: modelId });
      if (error) throw error;
      const outputs = data || {};

      // Chain: compute profitability + conversion metrics (non-blocking if RPC missing)
      try {
        const { data: metrics, error: mErr } = await supabase
          .rpc('compute_fee_model_metrics', { p_model_id: modelId });
        if (!mErr && metrics) {
          outputs.metrics = metrics;
        }
      } catch (_metricsErr) {
        console.warn('[FeeModels] compute_fee_model_metrics skipped (not deployed yet?)');
      }

      return { success: true, data: outputs };
    } catch (err) {
      console.error('[FeeModels] computeFeeModel error:', err);
      return { success: false, error: err.message, data: {} };
    }
  };

  // 6b) Standalone metrics computation (call separately if needed)
  const computeFeeModelMetrics = async (modelId) => {
    try {
      const { data, error } = await supabase
        .rpc('compute_fee_model_metrics', { p_model_id: modelId });
      if (error) throw error;
      return { success: true, data: data || {} };
    } catch (err) {
      console.error('[FeeModels] computeFeeModelMetrics error:', err);
      return { success: false, error: err.message, data: {} };
    }
  };

  // 7) Fetch active factor definitions from reference table
  const fetchFeeFactorDefinitions = async () => {
    try {
      const { data, error } = await supabase
        .from('fee_factor_definitions')
        .select('factor_key,label,description,sort_order,min_value,max_value,step_value,default_value,default_weight,sensitivity,is_active')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      console.error('[FeeModels] fetchFeeFactorDefinitions error:', err);
      return { success: false, error: err.message, data: [] };
    }
  };

  // 8) Helper: convert factor rows array → { [factor_key]: { value, weight } }
  const normalizeFactorsMap = (rows) => {
    const map = {};
    (rows || []).forEach(f => {
      map[f.factor_key] = { value: f.value, weight: f.weight };
    });
    return map;
  };

  // 9) Get latest inflation CPI year for a country code
  const getLatestInflationYear = async (countryCode) => {
    if (!countryCode) return { success: true, data: null };
    try {
      const { data, error } = await supabase
        .from('inflation_cpi_yearly')
        .select('year')
        .eq('country_code', countryCode)
        .order('year', { ascending: false })
        .limit(1);
      if (error) throw error;
      return { success: true, data: data?.[0]?.year ?? null };
    } catch (err) {
      console.error('[FeeModels] getLatestInflationYear error:', err);
      return { success: false, error: err.message, data: null };
    }
  };

  // 10) Compute inquiry conversion probability via RPC (Bayesian smoothed)
  const computeInquiryConversionProbability = async (inquiryId) => {
    try {
      const { data, error } = await supabase
        .rpc('compute_inquiry_conversion_probability', { p_inquiry_id: inquiryId });
      if (error) throw error;
      return { success: true, data: data || {} };
    } catch (err) {
      console.warn('[FeeModels] computeInquiryConversionProbability error:', err);
      return { success: false, error: err.message, data: {} };
    }
  };

  // 11) Link inquiry ↔ project (bidirectional)
  //     Sets inquiries.converted_project_id AND projects.source_inquiry_id
  const linkInquiryToProject = async (inquiryId, projectId) => {
    try {
      await requireSession();
      // Check if the project already has a different source_inquiry_id
      const { data: proj, error: pErr } = await supabase
        .from('projects')
        .select('id, source_inquiry_id')
        .eq('id', projectId)
        .maybeSingle();
      if (pErr) throw pErr;
      if (proj?.source_inquiry_id && proj.source_inquiry_id !== inquiryId) {
        return {
          success: false,
          error: 'conflict',
          existingInquiryId: proj.source_inquiry_id,
        };
      }
      // Update project → source_inquiry_id
      const { error: e1 } = await supabase
        .from('projects')
        .update({ source_inquiry_id: inquiryId })
        .eq('id', projectId);
      if (e1) throw e1;
      // Update inquiry → converted_project_id + status
      const { error: e2 } = await supabase
        .from('inquiries')
        .update({
          converted_project_id: projectId,
          status: 'converted',
          converted: true,
        })
        .eq('id', inquiryId);
      if (e2) throw e2;
      return { success: true };
    } catch (err) {
      console.error('[FeeModels] linkInquiryToProject error:', err);
      return { success: false, error: err.message };
    }
  };

  // 12a) Suggest top-N fee comparables via RPC (uses compute_similarity_score
  //      so suggestions rank exactly like the post-add badge).
  // 10b) Inflation-adjusted Avg Loaded Cost for a fee model. Mirrors
  //      the way compute_fee_model inflates per-comparable fee rates,
  //      so cost benchmark and fee benchmark sit in the same rupee.
  //      Reads from project_loaded_costs cache (no recompute).
  const computeLoadedCostMetrics = async (modelId) => {
    if (!modelId) return { success: true, data: null };
    try {
      const { data, error } = await supabase
        .rpc('compute_loaded_cost_metrics', { p_model_id: modelId });
      if (error) throw error;
      return { success: true, data: data || null };
    } catch (err) {
      console.warn('[FeeModels] computeLoadedCostMetrics error:', err);
      return { success: false, error: err.message, data: null };
    }
  };

  const suggestFeeComparables = async (modelId, topN = 10) => {
    if (!modelId) return { success: true, data: [] };
    try {
      const { data, error } = await supabase
        .rpc('suggest_fee_comparables', { p_model_id: modelId, p_top_n: topN });
      if (error) throw error;
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (err) {
      console.warn('[FeeModels] suggestFeeComparables error:', err);
      return { success: false, error: err.message, data: [] };
    }
  };

  // 12) Unlink inquiry from project (clears both sides)
  const unlinkInquiryFromProject = async (inquiryId, projectId) => {
    try {
      await requireSession();
      // Clear project.source_inquiry_id only if it matches this inquiry
      const { error: e1 } = await supabase
        .from('projects')
        .update({ source_inquiry_id: null })
        .eq('id', projectId)
        .eq('source_inquiry_id', inquiryId);
      if (e1) throw e1;
      // Clear inquiry.converted_project_id (do NOT change status automatically)
      const { error: e2 } = await supabase
        .from('inquiries')
        .update({ converted_project_id: null })
        .eq('id', inquiryId);
      if (e2) throw e2;
      return { success: true };
    } catch (err) {
      console.error('[FeeModels] unlinkInquiryFromProject error:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    createFeeModel,
    fetchFeeModelsForInquiry,
    upsertComparable,
    setComparablePurpose,
    fetchComparables,
    upsertFactor,
    fetchFactors,
    computeFeeModel,
    computeFeeModelMetrics,
    fetchFeeFactorDefinitions,
    normalizeFactorsMap,
    getLatestInflationYear,
    computeInquiryConversionProbability,
    computeLoadedCostMetrics,
    suggestFeeComparables,
    linkInquiryToProject,
    unlinkInquiryFromProject,
  };
};

// ============================================================================
// PROJECT LOADED COSTS — cached per-project loaded cost (see migration
// 20260520000004). The Profitability page populates this on render via
// upsert_project_loaded_costs; the Inquiries Fee Intelligence drawer
// reads it without re-running the heavy compute path.
// ============================================================================

export const useSupabaseProjectLoadedCosts = ({ enabled = true } = {}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('project_loaded_costs')
        .select('*');
      if (err) throw err;
      setRows(data || []);
    } catch (err) {
      console.error('[ProjectLoadedCosts] fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchAll();
  }, [enabled, fetchAll]);

  // Bulk upsert via RPC. records is an array shaped like the SQL
  // function expects (camelCase keys); see migration for details.
  const upsertMany = useCallback(async (records) => {
    if (!records || records.length === 0) return { success: true, count: 0 };
    try {
      await requireSession();
      const { data, error: err } = await supabase
        .rpc('upsert_project_loaded_costs', { p_records: records });
      if (err) throw err;
      return { success: true, count: data || 0 };
    } catch (err) {
      console.error('[ProjectLoadedCosts] upsert error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Per-project lookup: pid → { totalLoadedCostInr, projectSize, ... }
  const byProjectId = useMemo(() => {
    const m = {};
    for (const r of rows) {
      m[r.project_id] = {
        directLabourInr:     Number(r.direct_labour_inr) || 0,
        directExpensesInr:   Number(r.direct_expenses_inr) || 0,
        activityOverheadInr: Number(r.activity_overhead_inr) || 0,
        totalLoadedCostInr:  Number(r.total_loaded_cost_inr) || 0,
        projectSize:         r.project_size != null ? Number(r.project_size) : null,
        projectCountryCode:  r.project_country_code || null,
        projectStartYear:    r.project_start_year || null,
        computedAt:          r.computed_at,
      };
    }
    return m;
  }, [rows]);

  // Freshness signal: the oldest computed_at across all rows. UI uses
  // this to show "last computed: X ago".
  const oldestComputedAt = useMemo(() => {
    if (rows.length === 0) return null;
    let min = null;
    for (const r of rows) {
      if (!r.computed_at) continue;
      if (!min || r.computed_at < min) min = r.computed_at;
    }
    return min;
  }, [rows]);

  return { rows, byProjectId, oldestComputedAt, loading, error, refetch: fetchAll, upsertMany };
};

// ============================================================================
// VENDORS / CONTACTS DIRECTORY
// ============================================================================

export const useSupabaseVendors = ({ enabled = true } = {}) => {
  const [vendors, setVendors] = useState([]);
  const [vendorContacts, setVendorContacts] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  // Every project, deleted ones included, so a link to a project that was
  // since deleted still shows its name. Read by the BRIO Project writes to
  // turn the cell's names back into ids.
  const projectsRef = useRef([]);
  // False until vendor_project_links has been read. Before its migration runs
  // the table is missing, and the register then keeps the old behaviour:
  // BRIO Project read from and saved to vendors.brio_project as text.
  const linksReadyRef = useRef(false);

  // Supabase PostgREST has a server-side max-rows cap (default 1000) that
  // client .limit() cannot exceed. Paginate in 1000-row batches instead.
  const fetchAllPages = async (table, orderCol) => {
    const BATCH = 1000;
    let all = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table).select('*').order(orderCol).range(from, from + BATCH - 1);
      if (error) throw error;
      all = all.concat(data || []);
      if (!data || data.length < BATCH) break;
      from += BATCH;
    }
    return all;
  };

  const fetchVendors = async () => {
    setLoading(true);
    setError(null);
    try {
      const [vendorsData, contactsData, linksData, projectsRes] = await Promise.all([
        fetchAllPages('vendors', 'company_name'),
        fetchAllPages('vendor_contacts', 'name'),
        // BRIO Project links live in vendor_project_links, shared with the
        // Comms project facts drawer; vendors.brio_project keeps only names
        // that never matched a project.
        fetchAllPages('vendor_project_links', 'id').then(
          rows => { linksReadyRef.current = true; return rows; },
          err => { console.warn('vendor_project_links unavailable, using brio_project text:', err?.message); linksReadyRef.current = false; return []; },
        ),
        supabase.from('projects').select('id, name, deleted_at'),
      ]);
      if (projectsRes.error) throw projectsRes.error;
      const projects = (projectsRes.data || []).map(p => ({ id: normalizeId(p.id), name: p.name ?? '', deleted: !!p.deleted_at }));
      projectsRef.current = projects;
      const projectsById = new Map(projects.map(p => [p.id, p]));
      const projectIdsByVendor = new Map();
      (linksData || []).forEach(l => {
        const vid = normalizeId(l.vendor_id);
        const pid = normalizeId(l.project_id);
        if (!projectIdsByVendor.has(vid)) projectIdsByVendor.set(vid, []);
        const list = projectIdsByVendor.get(vid);
        if (!list.includes(pid)) list.push(pid);
      });

      setVendors((vendorsData || []).map(v => ({
        id: normalizeId(v.id),
        companyName: v.company_name ?? '',
        companyEmail: v.company_email ?? '',
        website: v.website ?? '',
        category: v.category ?? '',
        specialism: v.specialism ?? '',
        workedWithBefore: v.worked_with_before ?? '',
        // Linked project names, then legacy text: the same comma list the
        // cell always showed, so search, sort and filter read it unchanged.
        brioProject: projectDisplayList(projectIdsByVendor.get(normalizeId(v.id)) || [], projectsById, v.brio_project),
        projectIds: projectIdsByVendor.get(normalizeId(v.id)) || [],
        legacyBrioProject: v.brio_project ?? '',
        brioTeamInvolved: v.brio_team_involved ?? '',
        rating: v.rating != null ? Number(v.rating) : 0,
        location: v.location ?? '',
        street: v.street ?? '',
        city: v.city ?? '',
        state: v.state ?? '',
        country: v.country ?? '',
        postcode: v.postcode ?? '',
        dropboxUrl: v.dropbox_url ?? '',
        budget: v.budget ?? '',
        enrichmentSource: v.enrichment_source ?? '',
        enrichmentCheckedAt: v.enrichment_checked_at ?? null,
        phone: v.phone ?? '',
        instagram: v.instagram ?? '',
        lastEngaged: v.last_engaged ?? '',
        paymentTerms: v.payment_terms ?? '',
        notes: v.notes ?? '',
        commsEnabled: v.comms_enabled ?? false,
        createdAt: v.created_at,
        updatedAt: v.updated_at,
      })));

      setVendorContacts((contactsData || []).map(c => ({
        id: normalizeId(c.id),
        vendorId: normalizeId(c.vendor_id),
        name: c.name ?? '',
        designation: c.designation ?? '',
        phone: c.phone ?? '',
        altPhone: c.alt_phone ?? '',
        fax: c.fax ?? '',
        email: c.email ?? '',
        linkedin: c.linkedin ?? '',
        instagram: c.instagram ?? '',
        notes: c.notes ?? '',
        createdAt: c.created_at,
      })));
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError(formatSupabaseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enabled) fetchVendors(); }, [enabled]);

  // Without the project list every name would read as legacy text and the
  // sync would delete the vendor's links, so refuse rather than guess.
  const resolveBrioProject = (value) => {
    if (!linksReadyRef.current) return { ids: [], legacy: [String(value || '')].filter(Boolean), textOnly: true };
    if (!projectsRef.current.length) throw new Error('Projects have not loaded yet; try again in a moment.');
    return resolveProjectNames(value, projectsRef.current);
  };

  // Point a vendor's BRIO Project links at exactly these projects. A project
  // taken off removes every link the vendor has to it (whatever specialism
  // Comms filed it under); a project added gets a plain link that follows the
  // vendor's own specialism. Reads the current links from the database rather
  // than from state, so two open tabs cannot undo each other's adds.
  const syncVendorProjectLinks = async (vendorId, projectIds) => {
    const { data: current, error: readErr } = await supabase
      .from('vendor_project_links').select('project_id').eq('vendor_id', vendorId);
    if (readErr) throw readErr;
    const { add, remove } = diffIds((current || []).map(r => normalizeId(r.project_id)), projectIds);
    if (remove.length) {
      const { error: delErr } = await supabase
        .from('vendor_project_links').delete().eq('vendor_id', vendorId).in('project_id', remove);
      if (delErr) throw delErr;
    }
    if (add.length) {
      // One row at a time: the unique index is on an expression, which an
      // upsert cannot target, and a duplicate here only means another tab
      // added the same link a moment ago.
      for (const pid of add) {
        const { error: insErr } = await supabase
          .from('vendor_project_links').insert({ vendor_id: vendorId, project_id: pid });
        if (insErr && insErr.code !== '23505') throw insErr;
      }
    }
  };

  const createVendor = async (data) => {
    try {
      await requireSession();
      const dbData = {
        company_name: data.companyName || '',
        company_email: data.companyEmail || '',
        website: data.website || '',
        category: data.category || '',
        specialism: data.specialism || '',
        worked_with_before: data.workedWithBefore || '',
        brio_project: '',
        brio_team_involved: data.brioTeamInvolved || '',
        rating: data.rating != null ? Number(data.rating) : 0,
        location: data.location || '',
        street: data.street || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || '',
        postcode: data.postcode || '',
        dropbox_url: data.dropboxUrl || '',
        budget: data.budget || '',
        phone: data.phone || '',
        instagram: data.instagram || '',
        last_engaged: data.lastEngaged || null,
        payment_terms: data.paymentTerms || '',
        notes: data.notes || '',
      };
      // Names that match a project become links; the rest stay as legacy text.
      const { ids: projectIds, legacy } = data.brioProject
        ? resolveBrioProject(data.brioProject)
        : { ids: [], legacy: [] };
      dbData.brio_project = legacy.join(', ');
      const { data: inserted, error } = await supabase.from('vendors').insert([dbData]).select('id').single();
      if (error) throw error;
      if (inserted && projectIds.length && linksReadyRef.current) await syncVendorProjectLinks(normalizeId(inserted.id), projectIds);
      await fetchVendors();
      return { success: true, id: inserted ? normalizeId(inserted.id) : null };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'vendor' }) };
    }
  };

  const updateVendor = async (vendorId, updates) => {
    try {
      await requireSession();
      const dbUpdates = {};
      if (updates.companyName !== undefined) dbUpdates.company_name = updates.companyName;
      if (updates.companyEmail !== undefined) dbUpdates.company_email = updates.companyEmail;
      if (updates.website !== undefined) dbUpdates.website = updates.website;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.specialism !== undefined) dbUpdates.specialism = updates.specialism;
      if (updates.workedWithBefore !== undefined) dbUpdates.worked_with_before = updates.workedWithBefore;
      if (updates.brioProject !== undefined) {
        const { ids: projectIds, legacy, textOnly } = resolveBrioProject(updates.brioProject);
        dbUpdates.brio_project = legacy.join(', ');
        if (!textOnly) await syncVendorProjectLinks(vendorId, projectIds);
      }
      if (updates.brioTeamInvolved !== undefined) dbUpdates.brio_team_involved = updates.brioTeamInvolved;
      if (updates.rating !== undefined) dbUpdates.rating = Number(updates.rating);
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.street !== undefined) dbUpdates.street = updates.street;
      if (updates.city !== undefined) dbUpdates.city = updates.city;
      if (updates.state !== undefined) dbUpdates.state = updates.state;
      if (updates.country !== undefined) dbUpdates.country = updates.country;
      if (updates.postcode !== undefined) dbUpdates.postcode = updates.postcode;
      if (updates.dropboxUrl !== undefined) dbUpdates.dropbox_url = updates.dropboxUrl;
      if (updates.budget !== undefined) dbUpdates.budget = updates.budget;
      if (updates.enrichmentSource !== undefined) dbUpdates.enrichment_source = updates.enrichmentSource;
      if (updates.enrichmentCheckedAt !== undefined) dbUpdates.enrichment_checked_at = updates.enrichmentCheckedAt;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.instagram !== undefined) dbUpdates.instagram = updates.instagram;
      if (updates.lastEngaged !== undefined) dbUpdates.last_engaged = updates.lastEngaged || null;
      if (updates.paymentTerms !== undefined) dbUpdates.payment_terms = updates.paymentTerms;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
      if (updates.commsEnabled !== undefined) dbUpdates.comms_enabled = updates.commsEnabled;
      dbUpdates.updated_at = new Date().toISOString();
      const { error } = await supabase.from('vendors').update(dbUpdates).eq('id', vendorId);
      if (error) throw error;
      await fetchVendors();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'vendor' }) };
    }
  };

  const deleteVendor = async (vendorId) => {
    try {
      await requireSession();
      const { error } = await supabase.from('vendors').delete().eq('id', vendorId);
      if (error) throw error;
      await fetchVendors();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'vendor' }) };
    }
  };

  // ── Vendor Contacts CRUD ───────────────────────────────────────────────

  const createVendorContact = async (data) => {
    try {
      await requireSession();
      const dbData = {
        vendor_id: data.vendorId,
        name: data.name || '',
        designation: data.designation || '',
        phone: data.phone || '',
        alt_phone: data.altPhone || '',
        fax: data.fax || '',
        email: data.email || '',
        linkedin: data.linkedin || '',
        instagram: data.instagram || '',
        notes: data.notes || '',
      };
      const { error } = await supabase.from('vendor_contacts').insert([dbData]);
      if (error) throw error;
      await fetchVendors();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'vendor contact' }) };
    }
  };

  const updateVendorContact = async (contactId, updates) => {
    try {
      await requireSession();
      const dbUpdates = {};
      if (updates.vendorId !== undefined) dbUpdates.vendor_id = updates.vendorId;
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.designation !== undefined) dbUpdates.designation = updates.designation;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.altPhone !== undefined) dbUpdates.alt_phone = updates.altPhone;
      if (updates.fax !== undefined) dbUpdates.fax = updates.fax;
      if (updates.email !== undefined) dbUpdates.email = updates.email;
      if (updates.linkedin !== undefined) dbUpdates.linkedin = updates.linkedin;
      if (updates.instagram !== undefined) dbUpdates.instagram = updates.instagram;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
      const { error } = await supabase.from('vendor_contacts').update(dbUpdates).eq('id', contactId);
      if (error) throw error;
      await fetchVendors();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'vendor contact' }) };
    }
  };

  const deleteVendorContact = async (contactId) => {
    try {
      await requireSession();
      const { error } = await supabase.from('vendor_contacts').delete().eq('id', contactId);
      if (error) throw error;
      await fetchVendors();
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'vendor contact' }) };
    }
  };

  return {
    vendors, vendorContacts, loading, error,
    createVendor, updateVendor, deleteVendor,
    createVendorContact, updateVendorContact, deleteVendorContact,
    refetch: fetchVendors,
  };
};

// ============================================================================
// RLS COMPLIANCE SUMMARY
// ============================================================================
// 
// TASK A - Removed client-supplied 'id' from inserts (DB generates these):
//   ✅ createUser: removed id
//   ✅ createClient: removed id  
//   ✅ createProject: removed id
//   ✅ createTask: removed id
//   ✅ createHoliday: removed id
//   ⚠️  createCalendarEvent: KEPT id (TEXT type, client-generated UUID)
//
// Already correct (no id in insert):
//   ✅ createTimeEntry
//   ✅ createExpense
//   ✅ createAllocation
//   ✅ createTeam
//   ✅ createTimeOffRequest
//   ✅ createSalaryMatrixRevision
//   ✅ createOfficeOverhead (uses upsert)
//   ✅ upsertSalarySettings (uses upsert)
//   ✅ createFeeModel (DB-generated UUID)
//   ✅ upsertComparable (uses upsert)
//   ✅ upsertFactor (uses upsert)
//
// TASK B - Added requireSession() to all write operations:
//   ✅ All createX functions
//   ✅ All updateX functions  
//   ✅ All deleteX functions
//   ✅ All upsert functions
//   ✅ bulkUpdateTimeEntries
//   ✅ bulkDeleteTimeEntries
//   ✅ deleteTimeEntryNoRefetch
//   ✅ clearManualSalaryMarkup
//   ✅ createFeeModel, upsertComparable, upsertFactor (Fee Models hook)
//   ℹ️  fetchFeeFactorDefinitions: read-only reference table, no session needed
//   ℹ️  normalizeFactorsMap: pure helper, no DB access
//
// TASK C - No raw REST fetch calls found in this file
//   (All writes use supabase-js client)
//
// ============================================================================
